import * as THREE from 'three';
import {
  BLACK, BRASS, BRONZE, DSTEEL, DWOOD, GOLD, LEATHER_BELT, SKIN, STEEL, STRING, WOOD, cloth, emis, leather, metal, std, stone,
} from '../materials.ts';
import { G, M, V, flame, glowSprite, limb, ring } from '../prims.ts';
import { mulberry32 } from '../rand.ts';
import { shadowVictim } from './nightmares.ts';
import { LIGHT_K, begin, done, part, pointLight, type BaseOpts, type Model } from './types.ts';

/** Tower level: 4 is era II's mightiest (see `raise` in upgrades.ts for the pedestal). */
export type Level = 1 | 2 | 3 | 4;
export type MaterialTier = 1 | 2 | 3 | 4 | 5;

const ROYAL = '#1f3157';

/* ---------- iron (material 3, era II) ---------- */

/** Cold slate the stone takes under iron cladding (it reads apart from plain stone at the far camera). */
export const IRON_STONE = '#72767c';
/** Riveted plates: a cold blue-grey sheen. */
export const IRON_PLATE = metal('#6b7581', { roughness: 0.38, metalness: 0.55, flatShading: true });
/** Bands, straps and angle irons: near-black wrought iron. */
export const IRON_BAND = metal('#30353d', { roughness: 0.45, metalness: 0.65, flatShading: true });
/** Rivet heads and worn edges catching the light. */
export const IRON_RIVET = metal('#b9c1cb', { roughness: 0.25, flatShading: true });

/** A riveted iron plate of w × h on a face: its center at (x, y, z), turned `ry` (0 faces +Z). */
export const plate = (parent: THREE.Object3D, x: number, y: number, z: number, w: number, h: number, ry: number, rivets = true, tilt = 0): void => {
  const p = G(parent, x, y, z);
  p.rotation.order = 'YXZ';
  p.rotation.set(-tilt, ry, 0);
  M(new THREE.BoxGeometry(w, h, 0.014), IRON_PLATE, p);
  if (!rivets) return;
  const n = Math.max(2, Math.round(w / 0.09));
  for (const ry2 of [h / 2 - 0.025, -h / 2 + 0.025]) {
    for (let i = 0; i < n; i++) {
      M(new THREE.OctahedronGeometry(0.011, 0), IRON_RIVET, p).position.set(-w / 2 + 0.03 + (i * (w - 0.06)) / (n - 1), ry2, 0.009);
    }
  }
};

/** A pennant on a pole; returns the cloth so callers may wave it. */
const banner = (parent: THREE.Object3D, x: number, y0: number, z: number, h: number, color = ROYAL, w = 0.22): THREE.Mesh => {
  limb(parent, V(x, y0, z), V(x, y0 + h, z), 0.012, 0.012, DWOOD, 5);
  const b = M(new THREE.BoxGeometry(w, w * 1.3, 0.01), cloth(color), parent);
  b.position.set(x + w / 2 + 0.01, y0 + h - w * 0.7, z);
  return b;
};

/**
 * Iron cladding on a round body (a CylinderGeometry of `sides`, radius rBot at y0 to rTop at y1, turned `rot`):
 * a skirt of riveted plates on every face up to `skirt`, and riveted bands at the heights in `bands`.
 * `skip` leaves faces bare (doors, slits), by index.
 */
export const roundClad = (
  parent: THREE.Object3D, rBot: number, rTop: number, y0: number, y1: number, sides: number, rot: number, skirt: number, bands: number[], skip: number[] = [],
): void => {
  const rAt = (y: number): number => rBot + ((rTop - rBot) * (y - y0)) / (y1 - y0);
  const tilt = Math.atan2(rBot - rTop, y1 - y0);
  const k = Math.cos(Math.PI / sides);
  if (skirt > y0) {
    const yc = (y0 + 0.02 + skirt) / 2;
    const h = skirt - y0 - 0.02;
    for (let i = 0; i < sides; i++) {
      if (skip.includes(i)) continue;
      const a = rot + ((i + 0.5) * Math.PI * 2) / sides;
      const ap = rAt(yc) * k + 0.008;
      plate(parent, Math.sin(a) * ap, yc, Math.cos(a) * ap, 2 * rAt(yc) * Math.sin(Math.PI / sides) * 0.93, h, a, true, tilt);
    }
  }
  for (const y of bands) {
    const r = rAt(y) + 0.012;
    const b = M(new THREE.CylinderGeometry(r, r, 0.045, sides, 1, true), IRON_BAND, parent);
    b.position.y = y;
    b.rotation.y = rot;
    for (let i = 0; i < sides; i++) {
      const a = rot + ((i + 0.5) * Math.PI * 2) / sides;
      M(new THREE.OctahedronGeometry(0.012, 0), IRON_RIVET, parent).position.set(Math.sin(a) * (r * k + 0.006), y, Math.cos(a) * (r * k + 0.006));
    }
  }
};

/* ---------- wear (damage stages shared by every structure) ---------- */

export const ASHLAR = ['#958e82', '#8a8377', '#9e978b', '#857e73'];

/** Wear of a standing structure: 0 intact (≥ 70 % hp), 1 worn (35–70 %), 2 crumbling (< 35 %). */
export type Wear = 0 | 1 | 2;

const WEAR_STONE: Record<Wear, string> = { 0: '#8f887c', 1: '#847d71', 2: '#777066' };
/** The titan's stone of the era V ring, darkening with wear like the plain one. */
const TITAN_WEAR: Record<Wear, string> = { 0: '#454a53', 1: '#40444c', 2: '#3a3d44' };
const TITAN_DARK = '#2e3238';

/** A zigzag crack on a face: a few thin dark strokes in the local XY plane of a group at (x, y, z), turned `ry`. */
export const crack = (parent: THREE.Object3D, x: number, y: number, z: number, ry: number, size: number, rng: () => number): void => {
  const c = G(parent, x, y, z);
  c.rotation.y = ry;
  const mat = stone('#35312c');
  let px = 0;
  let py = 0;
  for (let i = 0; i < 4; i++) {
    const len = size * (0.2 + rng() * 0.25);
    // A gentle zigzag running down the face.
    const a = -Math.PI / 2 + (i % 2 ? 0.28 : -0.28) + (rng() - 0.5) * 0.3;
    const s = M(new THREE.BoxGeometry(0.01 + size * 0.015 * (1 - i * 0.2), len, 0.012), mat, c);
    s.rotation.z = a - Math.PI / 2;
    s.position.set(px + (Math.cos(a) * len) / 2, py + (Math.sin(a) * len) / 2, 0);
    px += Math.cos(a) * len;
    py += Math.sin(a) * len;
  }
};

/** A crack on a round body of radius `r`, at angle `a` (0 = +Z), its top at height `y`. */
export const crackRound = (parent: THREE.Object3D, r: number, y: number, a: number, size: number, rng: () => number): void =>
  crack(parent, Math.sin(a) * (r + 0.006), y, Math.cos(a) * (r + 0.006), a, size, rng);

/** A soot or damp stain on a face (local XY plane of a point at (x, y, z) turned `ry`), or lying flat on a top. */
export const stain = (parent: THREE.Object3D, x: number, y: number, z: number, ry: number, s: number, rng: () => number, color = '#4b463f', flat = false): void => {
  // A ragged outline, so it reads as a smudge and not a hole.
  const geo = new THREE.CircleGeometry(s, 9);
  const pos = geo.attributes.position as THREE.BufferAttribute;
  for (let i = 1; i < pos.count; i++) {
    const k = 0.65 + rng() * 0.5;
    pos.setXY(i, pos.getX(i) * k, pos.getY(i) * k);
  }
  const m = M(geo, std(color, { roughness: 1 }), parent);
  m.position.set(x, y, z);
  if (flat) m.rotation.set(-Math.PI / 2, 0, rng() * Math.PI);
  else m.rotation.y = ry;
  m.scale.y = 0.55 + rng() * 0.5;
};

/** Stones fallen at the foot of a worn piece, from `z0` outward (a negative `z0` drops them on the inner side). */
export const fallen = (parent: THREE.Object3D, n: number, spread: number, z0: number, rng: () => number): void => {
  const rock = stone('#7a7469');
  for (let i = 0; i < n; i++) {
    const s = 0.035 + rng() * 0.05;
    const m = i % 3 === 0
      ? M(new THREE.BoxGeometry(s * 2.2, s * 1.2, s * 1.6), stone(ASHLAR[Math.floor(rng() * ASHLAR.length)] as string), parent)
      : M(new THREE.DodecahedronGeometry(s, 0), rock, parent);
    m.position.set((rng() - 0.5) * spread, s * 0.6, z0 + Math.sign(z0) * rng() * 0.22);
    m.rotation.set(rng() * 0.6, rng() * Math.PI, rng() * 0.6);
  }
};

/** Stones fallen all around a footprint of radius `r` centered at (x, z), in the building's own stone colors. */
export const rubbleAround = (parent: THREE.Object3D, n: number, r: number, rng: () => number, colors: string[] = ASHLAR, x = 0, z = 0): void => {
  const rock = stone('#6d6559');
  for (let i = 0; i < n; i++) {
    const a = rng() * Math.PI * 2;
    const d = r + rng() * 0.2;
    const s = 0.03 + rng() * 0.05;
    const m = i % 2 === 0
      ? M(new THREE.BoxGeometry(s * 2.2, s * 1.3, s * 1.6), stone(colors[Math.floor(rng() * colors.length)] as string), parent)
      : M(new THREE.DodecahedronGeometry(s, 0), rock, parent);
    m.position.set(x + Math.sin(a) * d, s * 0.6, z + Math.cos(a) * d);
    m.rotation.set(rng() * 0.7, rng() * Math.PI, rng() * 0.7);
  }
};

/** Splintered planks and stake ends lying around a footprint of radius `r` centered at (x, z). */
export const splinters = (parent: THREE.Object3D, n: number, r: number, rng: () => number, x = 0, z = 0): void => {
  const charred = std('#2b2119', { roughness: 1 });
  for (let i = 0; i < n; i++) {
    const a = rng() * Math.PI * 2;
    const d = r + rng() * 0.2;
    const len = 0.12 + rng() * 0.18;
    const m = M(new THREE.BoxGeometry(0.035 + rng() * 0.02, 0.025, len), i % 4 === 3 ? charred : i % 2 ? DWOOD : WOOD, parent);
    m.position.set(x + Math.sin(a) * d, 0.015 + (i % 3) * 0.012, z + Math.cos(a) * d);
    m.rotation.set((rng() - 0.5) * 0.3, rng() * Math.PI, (rng() - 0.5) * 0.3);
  }
};

/** The jagged end of a snapped log or beam: a few thin slivers standing at (x, y, z). */
export const snapTop = (parent: THREE.Object3D, x: number, y: number, z: number, r: number, mat: THREE.Material, rng: () => number): void => {
  for (let i = 0; i < 3; i++) {
    const sp = M(new THREE.ConeGeometry(r * (0.35 + rng() * 0.2), r * (1.4 + rng() * 1.2), 4), mat, parent);
    const a = (i / 3) * Math.PI * 2 + rng();
    sp.position.set(x + Math.cos(a) * r * 0.45, y + r * 0.5, z + Math.sin(a) * r * 0.45);
    sp.rotation.set((rng() - 0.5) * 0.5, 0, (rng() - 0.5) * 0.5);
  }
};

/** Wear on an upgraded tower's pedestal (radius `r`, height `h`): cracks in stone, scorch and splinters on a timber deck. */
export const pedestalWear = (parent: THREE.Object3D, r: number, h: number, timber: boolean, wear: Wear): void => {
  if (wear === 0) return;
  const rng = mulberry32(950 + wear * 3 + (timber ? 1 : 0));
  if (timber) {
    for (const [x, z, ry] of [[0.15, r + 0.003, 0], [r + 0.003, -0.1, Math.PI / 2]] as const) stain(parent, x, h * 0.5, z, ry, Math.min(0.1, h * 0.4), rng, '#241c15');
    splinters(parent, wear * 2, r + 0.05, rng);
    return;
  }
  for (let i = 0; i < wear + 1; i++) crackRound(parent, r - 0.015, h - 0.02, 0.2 + i * 1.1 + rng() * 0.3, h * 0.6, rng);
};

/* ---------- crew ---------- */

type CrewPose = 'push' | 'haul' | 'carry' | 'channel' | 'pull' | 'guard';

interface CrewLook {
  /** Long robe and hood instead of tunic and helmet (acolytes). */
  robe?: boolean;
}

/** A small garrison figure (feet at the origin, facing +Z, ~1.05 × s tall) who works an engine or a tower. */
export const crewman = (parent: THREE.Object3D, x: number, y: number, z: number, yaw: number, pose: CrewPose, s = 0.45, tunic = ROYAL, look: CrewLook = {}): THREE.Group => {
  const f = G(parent, x, y, z);
  f.rotation.y = yaw;
  f.scale.setScalar(s);
  const coat = cloth(tunic);
  const boots = leather('#2a1c12');
  const lean = pose === 'haul' || pose === 'pull' ? -0.06 : pose === 'guard' ? 0 : 0.06;
  if (look.robe) {
    // A robe down to the ground.
    M(new THREE.CylinderGeometry(0.13, 0.24, 0.82, 9), coat, f).position.set(0, 0.41, lean * 0.5);
  } else {
    for (const lx of [-0.07, 0.07]) limb(f, V(lx, 0, (pose === 'haul' || pose === 'pull') && lx > 0 ? -0.08 : 0), V(lx, 0.42, lean * 0.5), 0.05, 0.045, boots, 5);
    const body = M(new THREE.CylinderGeometry(0.15, 0.18, 0.4, 8), coat, f);
    body.position.set(0, 0.62, lean);
    body.rotation.x = lean * 1.5;
  }
  M(new THREE.CylinderGeometry(0.185, 0.185, 0.05, 8), look.robe ? cloth('#8a86b8') : LEATHER_BELT, f).position.set(0, look.robe ? 0.56 : 0.46, lean * 0.6);
  M(new THREE.SphereGeometry(0.11, 8, 6), SKIN, f).position.set(0, 0.93, lean * 1.6);
  if (look.robe) {
    // Hood, open at the face.
    const hood = M(new THREE.SphereGeometry(0.14, 8, 6, Math.PI * 0.7, Math.PI * 1.6, 0, Math.PI * 0.62), coat, f);
    hood.position.set(0, 0.95, lean * 1.6);
    M(new THREE.ConeGeometry(0.08, 0.14, 6), coat, f).position.set(0, 1.1, lean * 1.6 - 0.05);
  } else {
    const helm = M(new THREE.SphereGeometry(0.125, 8, 4, 0, Math.PI * 2, 0, Math.PI / 2), STEEL, f);
    helm.position.set(0, 0.95, lean * 1.6);
    M(new THREE.CylinderGeometry(0.15, 0.15, 0.015, 10), STEEL, f).position.set(0, 0.95, lean * 1.6);
  }
  // Arms reach for the work: forward for pushing/aiming, low for hauling, up for carrying a stone,
  // raised to the crystal when channeling, overhead on a bell rope, at the side holding a spear.
  const hand =
    pose === 'push' ? [0.11, 0.66, 0.3]
    : pose === 'haul' ? [0.1, 0.52, 0.32]
    : pose === 'channel' ? [0.17, 1.12, 0.24]
    : pose === 'pull' ? [0.04, 1.08, 0.16]
    : pose === 'guard' ? [0.2, 0.55, 0.12]
    : [0.09, 0.98, 0.18];
  for (const sx of [-1, 1]) {
    const sh = V(sx * 0.19, 0.77, lean);
    const hd = V(sx * (hand[0] as number), hand[1] as number, (hand[2] as number) + lean);
    limb(f, sh, hd, 0.045, 0.035, coat, 5);
    M(new THREE.SphereGeometry(0.04, 6, 4), SKIN, f).position.copy(hd);
  }
  if (pose === 'carry') M(new THREE.DodecahedronGeometry(0.11), stone('#6d6559'), f).position.set(0, 1.08, 0.2 + lean);
  if (pose === 'guard') {
    limb(f, V(0.2, 0, 0.12), V(0.2, 1.45, 0.12), 0.018, 0.018, DWOOD, 5);
    M(new THREE.ConeGeometry(0.04, 0.16, 5), STEEL, f).position.set(0.2, 1.53, 0.12);
  }
  return f;
};

/* ---------- ballista ---------- */

export const ballistaOn = (parent: THREE.Object3D, y: number, wood: THREE.Material, armMat: THREE.Material, level: Level): THREE.Group => {
  const t = part(G(parent, 0, y, 0), 'turret');
  M(new THREE.BoxGeometry(0.08, 0.08, 0.6), wood, t);
  const arms = level >= 4 ? [0.05, -0.02, -0.09] : level >= 3 ? [0, -0.07] : [0];
  // Level 4: the bow arms are steel-shod, three deep.
  const bow = level >= 4 && armMat === wood ? DSTEEL : armMat;
  for (const dy of arms) {
    const pts: THREE.Vector3[] = [];
    for (let i = 0; i <= 12; i++) {
      const a = -1 + (i / 12) * 2;
      pts.push(V(Math.sin(a) * 0.38, dy, 0.18 - Math.cos(a) * 0.12));
    }
    M(new THREE.TubeGeometry(new THREE.CatmullRomCurve3(pts), 14, 0.022, 5), bow, t);
    limb(t, pts[0]!, V(0, dy, -0.05), 0.004, 0.004, STRING, 3);
    limb(t, pts[12]!, V(0, dy, -0.05), 0.004, 0.004, STRING, 3);
  }
  const bolt = M(new THREE.ConeGeometry(level >= 4 ? 0.035 : 0.025, level >= 4 ? 0.17 : 0.12, 6), DSTEEL, t);
  bolt.rotation.x = Math.PI / 2;
  bolt.position.z = level >= 4 ? 0.38 : 0.36;
  if (level >= 4) {
    // An iron mantlet under the stock shields the crew, riveted and bossed.
    const mant = M(new THREE.BoxGeometry(0.36, 0.13, 0.025), DSTEEL, t);
    mant.position.set(0, -0.1, 0.16);
    mant.rotation.x = -0.25;
    for (const x of [-0.13, 0, 0.13]) M(new THREE.SphereGeometry(0.018, 6, 4), STEEL, t).position.set(x, -0.09, 0.175);
  }
  M(new THREE.CylinderGeometry(0.04, 0.06, 0.2, 8), wood, t).position.y = -0.12;
  if (level >= 2) {
    for (const z of [-0.2, 0.05]) M(new THREE.BoxGeometry(0.1, 0.1, 0.03), DSTEEL, t).position.z = z;
    // Winch at the back.
    const w = M(new THREE.CylinderGeometry(0.035, 0.035, 0.18, 8), wood, t);
    w.rotation.z = Math.PI / 2;
    w.position.set(0, 0.02, -0.28);
  }
  if (level >= 3) {
    // Rack of spare bolts.
    for (const x of [-0.07, 0.07]) {
      const b = M(new THREE.CylinderGeometry(0.008, 0.008, 0.4, 4), DWOOD, t);
      b.rotation.x = Math.PI / 2;
      b.position.set(x, -0.05, 0.02);
    }
  }
  // The crew turns with the engine, so the soldier follows every aim.
  crewman(t, 0, -0.18, -0.36, 0, 'push', 0.42);
  return t;
};

export interface TowerOpts extends BaseOpts {
  level?: Level;
  /** Damage stage: 1 worn, 2 crumbling (see Wear). */
  wear?: Wear;
}
export interface BallistaOpts extends TowerOpts { material?: MaterialTier }

/**
 * Worn (1): a couple of stakes snapped (wood) or merlons knocked off (stone), soot and cracks, debris at
 * the foot. Crumbling (2): half the palisade broken and one stake leaning out, the rope binding gone
 * (wood); most of the crown gone, a block torn out of the face and long cracks (stone).
 */
export const ballista = (o?: BallistaOpts): Model => {
  const tier = o?.material ?? 1;
  const level = o?.level ?? 1;
  const wear = o?.wear ?? 0;
  const c = begin(o, 0.62, 50 + tier);
  const { g, rng } = c;
  // Separate stream for the damage, so a worn tower is the intact one with damage on top.
  const wr = mulberry32(50 + tier * 7 + wear);
  let turret: THREE.Group;
  const spin: THREE.Object3D[] = [];
  let wave: THREE.Mesh | undefined;
  let top = 1.45;
  if (tier === 1) {
    M(new THREE.CylinderGeometry(0.36, 0.4, 1.2, 10), std('#8a6a4a', { roughness: 1 }), g).position.y = 0.6;
    // Stakes 0–3 face the usual table camera (+X +Z), so the damage starts there.
    const snapped = wear === 2 ? [0, 1, 3, 4, 8, 10] : wear === 1 ? [1, 4, 9] : [];
    const gone = wear === 1 ? [3] : [];
    for (let i = 0; i < 12; i++) {
      const a = (i / 12) * Math.PI * 2;
      const h = 1.25 + rng() * 0.1;
      const x = Math.cos(a) * 0.42;
      const z = Math.sin(a) * 0.42;
      if (gone.includes(i)) {
        // Torn out: a dark gap in the palisade, the stake lying at the foot.
        const gap = M(new THREE.BoxGeometry(0.11, 1.0, 0.02), std('#3a2a1c', { roughness: 1 }), g);
        gap.position.set(Math.cos(a) * 0.395, 0.6, Math.sin(a) * 0.395);
        gap.rotation.y = Math.PI / 2 - a;
        snapTop(g, x, 0.1, z, 0.06, WOOD, wr);
        const fallenStake = G(g, Math.cos(a + 0.35) * 0.62, 0.06, Math.sin(a + 0.35) * 0.62);
        fallenStake.rotation.y = -a + 1.2;
        M(new THREE.CylinderGeometry(0.06, 0.065, h * 0.55, 6), WOOD, fallenStake).rotation.z = Math.PI / 2;
        continue;
      }
      if (wear === 2 && i === 2) {
        // Knocked loose at the foot: leans well out over the field, still pointed.
        const lean = G(g, x, 0, z);
        lean.rotation.set(Math.sin(a) * 0.6, 0, -Math.cos(a) * 0.6);
        M(new THREE.CylinderGeometry(0.06, 0.065, h, 6), WOOD, lean).position.y = h / 2 + 0.02;
        M(new THREE.ConeGeometry(0.06, 0.1, 6), WOOD, lean).position.y = h + 0.07;
        continue;
      }
      if (snapped.includes(i)) {
        const hs = h * (0.42 + wr() * 0.25);
        M(new THREE.CylinderGeometry(0.06, 0.065, hs, 6), WOOD, g).position.set(x, hs / 2, z);
        snapTop(g, x, hs, z, 0.06, WOOD, wr);
        continue;
      }
      M(new THREE.CylinderGeometry(0.06, 0.065, h, 6), WOOD, g).position.set(x, 0.62, z);
      M(new THREE.ConeGeometry(0.06, 0.1, 6), WOOD, g).position.set(x, 1.32, z);
    }
    if (wear < 2) ring(g, 0.43, 0.02, std('#a58a5a'), 0.95, 4, 20);
    if (wear > 0) {
      // Scorch where fire arrows struck the stakes, mostly on the side the table sees.
      for (let i = 0; i < wear * 3; i++) {
        const a = i < 3 ? 0.2 + i * 0.45 + wr() * 0.2 : wr() * Math.PI * 2;
        stain(g, Math.sin(a) * 0.49, 0.4 + wr() * 0.55, Math.cos(a) * 0.49, a, 0.13 + wr() * 0.05, wr, '#241c15');
      }
      splinters(g, wear === 2 ? 10 : 6, 0.5, wr);
    }
    if (level >= 2) ring(g, 0.44, 0.02, DSTEEL, 0.35, 4, 20);
    M(new THREE.CylinderGeometry(0.45, 0.45, 0.06, 14), DWOOD, g).position.y = 1.25;
    turret = ballistaOn(g, 1.45, WOOD, WOOD, level);
  } else if (tier === 2 || tier === 3) {
    const st = stone(tier === 2 ? '#8b857a' : IRON_STONE);
    M(new THREE.CylinderGeometry(0.44, 0.52, 1.25, 10), st, g).position.y = 0.62;
    M(new THREE.CylinderGeometry(0.53, 0.5, 0.12, 10), st, g).position.y = 1.3;
    const lost = wear === 2 ? [0, 1, 3, 4, 6] : wear === 1 ? [2, 5] : [];
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2;
      if (lost.includes(i)) {
        // A stub of the merlon is left on the crown.
        if (wear === 2 && i % 3 === 0) {
          const stub = M(new THREE.BoxGeometry(0.16, 0.06 + wr() * 0.05, 0.12), st, g);
          stub.position.set(Math.cos(a) * 0.47, 1.38, Math.sin(a) * 0.47);
          stub.rotation.set(0, -a, (wr() - 0.5) * 0.3);
        }
        continue;
      }
      const mr = M(new THREE.BoxGeometry(0.16, 0.18, 0.12), st, g);
      mr.position.set(Math.cos(a) * 0.47, 1.45, Math.sin(a) * 0.47);
      mr.rotation.y = -a;
      if (tier === 3) M(new THREE.ConeGeometry(0.025, 0.14, 5), DSTEEL, g).position.set(Math.cos(a) * 0.47, 1.6, Math.sin(a) * 0.47);
    }
    const mortar = std('#3a3632');
    for (let i = 0; i < 3; i++) ring(g, 0.49 - i * 0.025, 0.006, mortar, 0.3 + i * 0.35, 3, 24);
    M(new THREE.BoxGeometry(0.18, 0.3, 0.05), BLACK, g).position.set(0, 0.85, 0.47);
    if (tier === 3) {
      // Iron-clad: a riveted skirt, two bands, an iron-rimmed crown.
      roundClad(g, 0.52, 0.44, 0, 1.25, 10, 0, 0.55, [0.62, 1.12]);
      M(new THREE.CylinderGeometry(0.545, 0.545, 0.05, 10, 1, true), IRON_BAND, g).position.y = 1.3;
    } else if (level >= 2) {
      ring(g, 0.52, 0.02, DSTEEL, 1.18, 4, 24);
    }
    turret = ballistaOn(g, 1.55, DWOOD, tier === 3 ? DSTEEL : WOOD, level);
    top = 1.55;
    if (wear === 2) {
      // A block torn out of the face, the hole dark, the block at the foot.
      const a = 2.2;
      const hole = M(new THREE.BoxGeometry(0.2, 0.14, 0.04), stone('#2f2b27'), g);
      hole.position.set(Math.sin(a) * 0.475, 0.5, Math.cos(a) * 0.475);
      hole.rotation.y = a;
      const blk = M(new THREE.BoxGeometry(0.2, 0.13, 0.14), st, g);
      blk.position.set(Math.sin(a) * 0.78, 0.065, Math.cos(a) * 0.78);
      blk.rotation.set(0.2, a + 0.5, 0.1);
    }
  } else if (tier === 4) {
    const st = stone('#4d4a55');
    M(new THREE.CylinderGeometry(0.48, 0.55, 0.5, 8), st, g).position.y = 0.25;
    const cm = emis('#a8e6ff', '#3aa6ff', 0.7, { flatShading: true, transparent: true, opacity: 0.85, roughness: 0.1 });
    for (let i = 0; i < 4; i++) {
      const a = (i / 4) * Math.PI * 2 + 0.4;
      const cr = M(new THREE.OctahedronGeometry(0.13), cm, g);
      cr.scale.y = 4.5;
      cr.position.set(Math.cos(a) * 0.34, 1.0, Math.sin(a) * 0.34);
    }
    const core = M(new THREE.OctahedronGeometry(0.16), cm, g);
    core.position.y = 1.0;
    core.scale.y = 2;
    spin.push(core);
    glowSprite(g, '#3aa6ff', 0.9, V(0, 1, 0), 0.5);
    M(new THREE.CylinderGeometry(0.42, 0.42, 0.05, 8), st, g).position.y = 1.55;
    turret = ballistaOn(g, 1.72, DWOOD, cm, level);
    top = 1.72;
    const halo = G(g, 0, 0.7, 0);
    const rm = emis('#cfefff', '#5ab8ff', 1);
    ring(halo, 0.62, 0.008, rm, 0, 4, 36);
    for (let i = 0; i < 10; i++) {
      const gl = M(new THREE.BoxGeometry(0.05, 0.05, 0.004), rm, halo);
      const a = (i / 10) * Math.PI * 2;
      gl.position.set(Math.cos(a) * 0.62, 0, Math.sin(a) * 0.62);
      gl.rotation.y = -a + Math.PI / 2;
    }
    spin.push(halo);
  } else {
    const st = stone('#e3e0d6'), mith = metal('#dfe6ee', { roughness: 0.2 }), rune = emis('#ffe3a0', '#ffb020', 1);
    M(new THREE.CylinderGeometry(0.44, 0.52, 1.3, 12), st, g).position.y = 0.65;
    for (let i = 0; i < 3; i++) ring(g, 0.5 - i * 0.02, 0.025, mith, 0.12 + i * 0.5, 5, 28);
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * Math.PI * 2;
      const r = M(new THREE.BoxGeometry(0.04, 0.12, 0.01), rune, g);
      r.position.set(Math.cos(a) * 0.495, 0.85, Math.sin(a) * 0.495);
      r.rotation.y = -a + Math.PI / 2;
    }
    M(new THREE.CylinderGeometry(0.55, 0.5, 0.12, 12), mith, g).position.y = 1.34;
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * Math.PI * 2;
      M(new THREE.ConeGeometry(0.05, 0.22, 6), mith, g).position.set(Math.cos(a) * 0.46, 1.5, Math.sin(a) * 0.46);
    }
    limb(g, V(-0.3, 1.35, -0.3), V(-0.3, 2.3, -0.3), 0.012, 0.012, mith, 5);
    wave = M(new THREE.BoxGeometry(0.3, 0.4, 0.01), cloth('#1f3a6e'), g);
    wave.position.set(-0.15, 2.05, -0.3);
    turret = ballistaOn(g, 1.6, DWOOD, mith, level);
    glowSprite(g, '#ffc65a', 0.5, V(0, 0.85, 0.5), 0.3);
    top = 1.6;
  }
  if (level >= 2 && tier !== 5) banner(g, 0.32, top - 0.1, -0.32, 0.75);
  if (level >= 3 && wear < 2) banner(g, -0.36, top - 0.1, -0.2, 0.6, '#6e1f24', 0.18);
  if (wear > 0 && tier > 1) {
    const r = tier === 4 ? 0.52 : 0.5;
    for (let i = 0; i < wear * 2 + 1; i++) crackRound(g, r, (tier === 4 ? 0.45 : 1.15) - wr() * 0.2, (i / (wear * 2 + 1)) * Math.PI * 2 + wr(), wear === 2 ? 0.42 : 0.26, wr);
    for (let i = 0; i < wear * 2; i++) {
      const a = wr() * Math.PI * 2;
      stain(g, Math.sin(a) * (r + 0.004), 0.3 + wr() * 0.7, Math.cos(a) * (r + 0.004), a, 0.1 + wr() * 0.06, wr);
    }
    rubbleAround(g, wear === 2 ? 12 : 5, 0.55, wr, tier === 2 ? ASHLAR : ['#5f5b55', '#4d4a55', '#6b655c']);
  }
  const t = (n: number): void => {
    turret.rotation.y = Math.sin(n * 0.22) * 0.7;
    for (const s of spin) s.rotation.y = n * (s === spin[0] && tier === 4 ? 0.2 : 0.12);
    if (wave) wave.scale.x = 1 + (n % 8 < 4 ? 0.05 : -0.05);
  };
  return done(c, top + 0.25, t);
};

/* ---------- catapult ---------- */

/**
 * Worn (1): scorched planks, a split rail end and splinters around. Crumbling (2): a wheel torn off and
 * lying beside it (the corner propped on a stone), one leg of the A-frame snapped, more scorch and wreckage.
 */
export const catapult = (o?: TowerOpts): Model => {
  const level = o?.level ?? 1;
  const wear = o?.wear ?? 0;
  const c = begin(o, 0.7, 56);
  const { g, R, rng } = c;
  const wr = mulberry32(560 + wear);
  M(new THREE.BoxGeometry(0.9, 0.08, 0.55), WOOD, g).position.y = 0.2;
  for (const z of [-0.24, 0.24]) M(new THREE.BoxGeometry(0.9, 0.1, 0.08), DWOOD, g).position.set(0, 0.22, z);
  for (const x of [-0.32, 0.32]) for (const z of [-0.3, 0.3]) {
    const off = wear === 2 && x > 0 && z > 0;
    const w = M(new THREE.CylinderGeometry(0.13, 0.13, 0.06, 10), DWOOD, g);
    const band = M(new THREE.TorusGeometry(0.13, 0.012, 4, 14), DSTEEL, g);
    if (off) {
      // Torn off the axle, lying flat in front; a stone props the corner up.
      w.position.set(0.62, 0.03, 0.55);
      band.rotation.x = Math.PI / 2;
      band.position.set(0.62, 0.065, 0.55);
      M(new THREE.BoxGeometry(0.14, 0.12, 0.12), stone('#6d6559'), g).position.set(x, 0.06, z - 0.02);
      continue;
    }
    w.rotation.x = Math.PI / 2;
    w.position.set(x, 0.13, z);
    band.position.set(x, 0.13, z + (z > 0 ? 0.03 : -0.03));
  }
  for (const z of [-0.2, 0.2]) {
    limb(g, V(-0.1, 0.25, z), V(0, 0.7, z), 0.03, 0.03, WOOD, 6);
    if (wear === 2 && z > 0) {
      // The front leg snapped halfway; its top half lies on the bed.
      limb(g, V(0.15, 0.25, z), V(0.09, 0.45, z), 0.03, 0.03, WOOD, 6);
      snapTop(g, 0.09, 0.44, z, 0.03, WOOD, wr);
      limb(g, V(0.2, 0.27, z + 0.06), V(0.45, 0.25, z - 0.05), 0.028, 0.028, WOOD, 6);
    } else limb(g, V(0.15, 0.25, z), V(0, 0.7, z), 0.03, 0.03, WOOD, 6);
  }
  if (wear > 0) {
    for (let i = 0; i < wear + 1; i++) stain(g, (wr() - 0.5) * 0.7, 0.242, (wr() - 0.5) * 0.36, 0, 0.07 + wr() * 0.05, wr, '#2e241b', true);
    // The front rail end split open.
    snapTop(g, -0.43, 0.26, 0.24, 0.05, DWOOD, wr);
    splinters(g, wear === 2 ? 9 : 4, 0.55, wr);
  }
  if (level >= 2) {
    for (const x of [-0.4, 0, 0.4]) for (const z of [-0.24, 0.24]) M(new THREE.BoxGeometry(0.04, 0.11, 0.09), DSTEEL, g).position.set(x, 0.22, z);
    const ax = M(new THREE.CylinderGeometry(0.025, 0.025, 0.5, 6), DSTEEL, g);
    ax.rotation.x = Math.PI / 2;
    ax.position.y = 0.7;
  }
  if (level >= 3) {
    banner(g, -0.42, 0.24, -0.24, 0.75);
    const rk = stone('#6d6559');
    for (let i = 0; i < 5; i++) M(new THREE.DodecahedronGeometry(0.06 + rng() * 0.02), rk, g).position.set(-0.32 + R() * 0.12, 0.3 + (i > 2 ? 0.07 : 0), R() * 0.2);
  }
  const arm = part(G(g, 0, 0.68, 0), 'arm');
  limb(arm, V(-0.3, 0, 0), V(0.55, 0, 0), 0.025, 0.025, WOOD, 6);
  const cup = M(new THREE.SphereGeometry(0.1, 8, 5, 0, Math.PI * 2, 0, Math.PI / 2), DWOOD, arm);
  cup.rotation.x = Math.PI;
  cup.position.set(0.55, 0.05, 0);
  const rock = M(new THREE.DodecahedronGeometry(0.08), stone('#6d6559'), arm);
  rock.position.set(0.55, 0.1, 0);
  // Two crew: one winds the arm down, one brings the next stone.
  const crew = part(G(g, 0, 0, 0), 'crew');
  crewman(crew, -0.6, 0, 0.12, Math.PI / 2, 'haul', 0.5);
  crewman(crew, 0.5, 0, 0.5, Math.PI + 0.5, 'carry', 0.5, '#6e1f24');
  const cw = level >= 4 ? 0.25 : level >= 2 ? 0.2 : 0.16;
  M(new THREE.BoxGeometry(cw, cw, cw), stone('#4f4a44'), arm).position.set(-0.3, -0.12 - (level >= 4 ? 0.03 : 0), 0);
  if (level >= 4) {
    // The counterweight is caged in iron and the beam doubled with straps.
    for (const y of [-0.21, -0.07]) M(new THREE.BoxGeometry(cw + 0.02, 0.025, cw + 0.02), DSTEEL, arm).position.set(-0.3, y, 0);
    limb(arm, V(-0.2, 0.035, 0), V(0.45, 0.035, 0), 0.018, 0.018, DWOOD, 6);
    for (const x of [0.05, 0.4]) M(new THREE.BoxGeometry(0.03, 0.09, 0.07), DSTEEL, arm).position.set(x, 0.015, 0);
    const crate = G(g, 0.3, 0.24, -0.12);
    M(new THREE.BoxGeometry(0.2, 0.1, 0.16), DWOOD, crate).position.y = 0.05;
    for (const x of [-0.1, 0.1]) M(new THREE.BoxGeometry(0.012, 0.11, 0.17), DSTEEL, crate).position.set(x, 0.05, 0);
    const rk = stone('#5f574d');
    for (let i = 0; i < 4; i++) M(new THREE.DodecahedronGeometry(0.045), rk, crate).position.set(-0.05 + (i % 2) * 0.1, 0.11, -0.04 + Math.floor(i / 2) * 0.08);
  }
  if (level >= 2) for (const x of [-0.1, 0.25]) M(new THREE.BoxGeometry(0.03, 0.065, 0.065), DSTEEL, arm).position.set(x, 0, 0);
  const t = (n: number): void => {
    const f = n % 24;
    arm.rotation.z = f < 3 ? 1.0 : f < 18 ? 0.1 + ((f - 3) / 15) * 0.15 : 0.25 - (f - 18) * 0.03;
    rock.visible = f >= 8;
  };
  t(8);
  return done(c, 1.0, t);
};

/* ---------- belfry ---------- */

/**
 * Worn (1): cracked faces, soot, a chipped corner and stones at the foot. Crumbling (2): one corner
 * pillar of the bell chamber broken, so the roof slumps toward it; slates and blocks on the ground.
 */
export const belfry = (o?: TowerOpts): Model => {
  const level = o?.level ?? 1;
  const wear = o?.wear ?? 0;
  const c = begin(o, 0.62, 57);
  const { g } = c;
  const wr = mulberry32(570 + wear);
  const st = stone('#857f74');
  M(new THREE.BoxGeometry(0.6, 1.3, 0.6), st, g).position.y = 0.65;
  for (const x of [-0.24, 0.24]) for (const z of [-0.24, 0.24]) {
    if (wear === 2 && x > 0 && z > 0) {
      // Broken short; the slumped roof rests on its top (1.645).
      M(new THREE.BoxGeometry(0.1, 0.35, 0.1), st, g).position.set(x, 1.295 + 0.175, z);
      crack(g, x, 1.6, z + 0.051, 0, 0.2, wr);
      continue;
    }
    M(new THREE.BoxGeometry(0.1, 0.55, 0.1), st, g).position.set(x, 1.57, z);
  }
  // Crumbling: the roof hinges on the far pillar (-X -Z) and drops 0.2 onto the broken one (+X +Z).
  const hinge = G(g, wear === 2 ? -0.24 : 0, wear === 2 ? 1.845 : 0, wear === 2 ? -0.24 : 0);
  if (wear === 2) hinge.rotation.set(0.21, 0, -0.21);
  const roof = M(new THREE.ConeGeometry(0.5, 0.55, 4), stone('#3e3a36'), hinge);
  roof.position.set(wear === 2 ? 0.24 : 0, wear === 2 ? 0.275 : 2.12, wear === 2 ? 0.24 : 0);
  roof.rotation.y = Math.PI / 4;
  if (wear > 0) {
    for (const [ry, fx, fz] of [[0, 0, 1], [Math.PI / 2, 1, 0], [Math.PI, 0, -1], [-Math.PI / 2, -1, 0]] as const) {
      const off = (wr() - 0.5) * 0.3;
      crack(g, fx * 0.303 + fz * off, 1.2 - wr() * 0.3, fz * 0.303 - fx * off, ry, wear === 2 ? 0.5 : 0.28, wr);
      if (wr() < 0.5 * wear) stain(g, fx * 0.302 - fz * off, 0.4 + wr() * 0.5, fz * 0.302 + fx * off, ry, 0.08 + wr() * 0.05, wr);
    }
    // A chipped top corner and a block knocked out of the face (it lies at the foot).
    M(new THREE.BoxGeometry(0.09, 0.07, 0.09), stone('#4a4640'), g).position.set(-0.27, 1.27, 0.27);
    for (let i = 0; i < wear; i++) {
      const x = i ? -0.14 : 0.13;
      M(new THREE.BoxGeometry(0.13, 0.09, 0.02), stone('#2f2b27'), g).position.set(x, i ? 0.45 : 1.05, 0.296);
      const blk = M(new THREE.BoxGeometry(0.13, 0.09, 0.1), st, g);
      blk.position.set(x + 0.08, 0.045, 0.45 + i * 0.08);
      blk.rotation.set(0.15, 0.5 + i, 0);
    }
    rubbleAround(g, wear === 2 ? 11 : 5, 0.42, wr, ['#857f74', '#7a746a', '#8f887c']);
    if (wear === 2) for (let i = 0; i < 3; i++) {
      const sl = M(new THREE.BoxGeometry(0.16, 0.025, 0.11), stone('#3e3a36'), g);
      sl.position.set(0.45 + wr() * 0.2, 0.02 + i * 0.01, 0.3 + wr() * 0.25);
      sl.rotation.set(wr() * 0.3, wr() * Math.PI, 0);
    }
  }
  M(new THREE.BoxGeometry(0.16, 0.28, 0.05), BLACK, g).position.set(0, 0.8, 0.31);
  const bell = part(G(g, 0, 1.75, 0), 'bell');
  const bm = metal('#9a6a2a', { side: THREE.DoubleSide, roughness: 0.4 });
  const bs = level >= 4 ? 1.28 : level >= 3 ? 1.15 : 1;
  M(new THREE.CylinderGeometry(0.09 * bs, 0.19 * bs, 0.26 * bs, 12, 1, true), bm, bell).position.y = -0.18;
  M(new THREE.SphereGeometry(0.035, 6, 4), DSTEEL, bell).position.y = -0.33 * bs;
  if (level >= 2) {
    for (const k of [-1, 1]) {
      const b = M(new THREE.BoxGeometry(0.2, 0.45, 0.01), cloth(ROYAL), g);
      b.position.set(k * 0.31, 1.0, 0);
      b.rotation.y = Math.PI / 2;
    }
    ring(bell, 0.17 * bs, 0.012, BRASS, -0.27 * bs, 4, 16);
  }
  if (level >= 3) {
    limb(g, V(0, 2.38, 0), V(0, 2.6, 0), 0.012, 0.012, DSTEEL, 5);
    M(new THREE.OctahedronGeometry(0.05), emis('#ffe0a0', '#ff4f3a', 0.9, { flatShading: true }), g).position.y = 2.62;
    for (const x of [-0.24, 0.24]) for (const z of [-0.24, 0.24]) M(new THREE.ConeGeometry(0.03, 0.1, 4), DSTEEL, g).position.set(x, 1.88, z);
  }
  if (level >= 4) {
    // Iron girdles round the shaft, a crimson hanging on the back face and the lamp lit for the field to see.
    for (const y of [0.42, 1.02]) M(new THREE.BoxGeometry(0.625, 0.045, 0.625), DSTEEL, g).position.y = y;
    const hang = M(new THREE.BoxGeometry(0.24, 0.5, 0.012), cloth('#7a1d22'), g);
    hang.position.set(0, 0.95, -0.31);
    M(new THREE.BoxGeometry(0.07, 0.07, 0.016), cloth('#d9dce3'), g).position.set(0, 1.0, -0.318);
    M(new THREE.BoxGeometry(0.28, 0.025, 0.03), DSTEEL, g).position.set(0, 1.21, -0.315);
    glowSprite(g, '#ff6a4a', 0.4, V(0, 2.62, 0), 0.7);
  }
  // The ringer pulls the bell rope from the doorstep.
  limb(g, V(0.12, 1.5, 0.31), V(0.13, 0.55, 0.52), 0.008, 0.008, STRING, 3);
  crewman(part(G(g, 0, 0, 0), 'crew'), 0.12, 0, 0.62, Math.PI, 'pull', 0.45);
  const waves: THREE.Mesh[] = [];
  for (let i = 0; i < 2; i++) {
    const w = ring(g, 0.35, 0.01, new THREE.MeshStandardMaterial({ color: '#ffe0a0', emissive: '#c9a24a', emissiveIntensity: 0.6, transparent: true, opacity: 0.5, depthWrite: false }), 1.6, 3, 32);
    waves.push(w);
  }
  const t = (n: number): void => {
    bell.rotation.z = n % 6 < 3 ? 0.3 : -0.3;
    waves.forEach((w, i) => {
      const p = ((n + i * 6) % 12) / 12;
      w.scale.setScalar(0.6 + p * 1.6);
      (w.material as THREE.MeshStandardMaterial).opacity = 0.5 * (1 - p);
    });
  };
  t(0);
  return done(c, level >= 3 ? 2.6 : 2.4, t);
};

/* ---------- arcane tower ---------- */

/**
 * Worn (1): cracked stone, one claw of the cradle broken and the crystal dimmer. Crumbling (2): two claws
 * gone, the crystal cracked, tilted and barely lit, a shard of it fallen and glowing faintly on the ground.
 */
export const arcane = (o?: TowerOpts): Model => {
  const level = o?.level ?? 1;
  const wear = o?.wear ?? 0;
  const c = begin(o, 0.62, 58);
  const { g } = c;
  const wr = mulberry32(580 + wear);
  const st = stone('#4d4a55');
  M(new THREE.CylinderGeometry(0.32, 0.45, 1.0, 8), st, g).position.y = 0.5;
  for (let i = 0; i < 4; i++) {
    const a = (i / 4) * Math.PI * 2 + 0.4;
    const from = V(Math.cos(a) * 0.3, 0.95, Math.sin(a) * 0.3);
    const to = V(Math.cos(a) * 0.2, 1.35, Math.sin(a) * 0.2);
    if ((wear >= 1 && i === 1) || (wear === 2 && i === 2)) {
      const mid = from.clone().lerp(to, 0.45);
      limb(g, from, mid, 0.05, 0.04, st, 6);
      snapTop(g, mid.x, mid.y - 0.02, mid.z, 0.035, st, wr);
      continue;
    }
    limb(g, from, to, 0.05, 0.03, st, 6);
  }
  const glow = [0.8, 0.45, 0.2][wear] as number;
  const cm = emis(wear === 2 ? '#7fa8bf' : '#a8e6ff', '#3aa6ff', glow, { flatShading: true, transparent: true, opacity: 0.88, roughness: 0.1 });
  const crystal = part(G(g, 0, 1.65, 0), 'crystal');
  const cr = M(new THREE.OctahedronGeometry(0.22), cm, crystal);
  cr.scale.y = 1.8;
  if (wear === 2) cr.rotation.z = 0.28;
  glowSprite(crystal, '#3aa6ff', [1.1, 0.7, 0.35][wear] as number, V(0, 0, 0), 0.55);
  if (wear > 0) {
    for (let i = 0; i < wear * 2 + 1; i++) crackRound(g, 0.37, 0.85 - wr() * 0.2, (i / (wear * 2 + 1)) * Math.PI * 2 + wr(), wear === 2 ? 0.4 : 0.25, wr);
    rubbleAround(g, wear === 2 ? 10 : 4, 0.48, wr, ['#4d4a55', '#5a5762', '#423f49']);
    if (wear === 2) {
      const shard = M(new THREE.OctahedronGeometry(0.07), emis('#7fa8bf', '#3aa6ff', 0.5, { flatShading: true, roughness: 0.1 }), g);
      shard.scale.y = 1.8;
      shard.position.set(-0.55, 0.06, 0.35);
      shard.rotation.z = 1.3;
      glowSprite(g, '#3aa6ff', 0.25, V(-0.55, 0.08, 0.35), 0.4);
    }
  }
  const rm = emis('#cfefff', '#5ab8ff', 1);
  const halo = G(crystal);
  if (level >= 2) {
    ring(halo, 0.36, 0.007, rm, -0.1, 3, 32);
    for (let i = 0; i < 6; i++) {
      const gl = M(new THREE.BoxGeometry(0.04, 0.04, 0.004), rm, halo);
      const a = (i / 6) * Math.PI * 2;
      gl.position.set(Math.cos(a) * 0.36, -0.1, Math.sin(a) * 0.36);
      gl.rotation.y = -a + Math.PI / 2;
    }
    ring(g, 0.41, 0.02, DSTEEL, 0.6, 4, 20);
  }
  if (level >= 3) {
    for (const k of [-1, 1]) {
      limb(g, V(k * 0.42, 0, 0.1), V(k * 0.42, 0.85, 0.1), 0.06, 0.045, st, 6);
      const s = M(new THREE.OctahedronGeometry(0.08), cm, g);
      s.scale.y = 1.8;
      s.position.set(k * 0.42, 1.0, 0.1);
      glowSprite(g, '#3aa6ff', 0.45, V(k * 0.42, 1.0, 0.1), 0.5);
    }
  }
  if (level >= 4) {
    // Two more pylons behind, a second, wider ring of runes round the crystal and an iron crown on the cradle.
    for (const k of [-1, 1]) {
      limb(g, V(k * 0.3, 0, -0.34), V(k * 0.3, 1.0, -0.34), 0.055, 0.04, st, 6);
      const s = M(new THREE.OctahedronGeometry(0.08), cm, g);
      s.scale.y = 2.1;
      s.position.set(k * 0.3, 1.17, -0.34);
      glowSprite(g, '#3aa6ff', 0.45, V(k * 0.3, 1.17, -0.34), 0.5);
    }
    ring(halo, 0.5, 0.006, rm, 0.12, 3, 40);
    for (let i = 0; i < 8; i++) {
      const gl = M(new THREE.BoxGeometry(0.035, 0.05, 0.004), rm, halo);
      const a = (i / 8) * Math.PI * 2 + 0.2;
      gl.position.set(Math.cos(a) * 0.5, 0.12, Math.sin(a) * 0.5);
      gl.rotation.y = -a + Math.PI / 2;
    }
    ring(g, 0.345, 0.025, DSTEEL, 0.97, 4, 20);
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2;
      M(new THREE.ConeGeometry(0.022, 0.1, 4), DSTEEL, g).position.set(Math.cos(a) * 0.34, 1.03, Math.sin(a) * 0.34);
    }
  }
  // An acolyte at the foot of the tower feeds the crystal.
  crewman(part(G(g, 0, 0, 0), 'crew'), 0.32, 0, 0.5, Math.PI + 0.55, 'channel', 0.46, '#2a2550', { robe: true });
  const L = pointLight(c, 0x5ab8ff, 0.9, 3, V(0, 1.65, 0));
  const lit = [1, 0.6, 0.3][wear] as number;
  const t = (n: number): void => {
    cr.rotation.y = n * 0.15;
    halo.rotation.y = -n * 0.1;
    crystal.position.y = 1.65 + (n % 12 < 6 ? 0.04 : 0);
    L.intensity = (0.7 + ((n % 8) / 8) * 0.4) * LIGHT_K * lit;
  };
  return done(c, 2.05, t);
};

/* ---------- beacon ---------- */

/**
 * Worn (1): cracks, soot up the shaft, a merlon gone and stones at the foot. Crumbling (2): half the crown
 * broken off, the brazier knocked askew and its fire guttering low, long cracks and a heap of stone.
 */
export const beacon = (o?: TowerOpts): Model => {
  const level = o?.level ?? 1;
  const wear = o?.wear ?? 0;
  const c = begin(o, 0.62, 59);
  const { g, rng } = c;
  const wr = mulberry32(590 + wear);
  const st = stone('#8b857a');
  M(new THREE.CylinderGeometry(0.3, 0.42, 1.7, 10), st, g).position.y = 0.85;
  M(new THREE.CylinderGeometry(0.4, 0.36, 0.12, 10), st, g).position.y = 1.74;
  const pan = M(new THREE.CylinderGeometry(0.22, 0.14, 0.18, 10), DSTEEL, g);
  pan.position.y = 1.9;
  if (wear === 2) pan.rotation.z = 0.18;
  const fl = flame(g, V(0, 1.98, 0), wear === 2 ? 0.75 : level >= 4 ? 1.75 : 1.4, rng);
  const L = pointLight(c, 0xff9a3a, wear === 2 ? 0.6 : 1.2, 4, V(0, 2.1, 0));
  const mh = level >= 4 ? 0.24 : level >= 3 ? 0.2 : 0.14;
  for (let i = 0; i < 4; i++) {
    const a = (i / 4) * Math.PI * 2;
    if ((wear >= 1 && i === 1) || (wear === 2 && i === 2)) continue;
    M(new THREE.BoxGeometry(0.12, mh, 0.1), st, g).position.set(Math.cos(a) * 0.36, 1.8 + mh / 2, Math.sin(a) * 0.36);
  }
  if (wear > 0) {
    for (let i = 0; i < wear * 2 + 1; i++) crackRound(g, 0.35, 1.45 - wr() * 0.4, (i / (wear * 2 + 1)) * Math.PI * 2 + wr(), wear === 2 ? 0.55 : 0.3, wr);
    // Soot climbing the shaft under the fire.
    for (let i = 0; i < wear * 2; i++) {
      const a = wr() * Math.PI * 2;
      stain(g, Math.sin(a) * 0.335, 1.45 + wr() * 0.15, Math.cos(a) * 0.335, a, 0.1, wr, '#2a2622');
    }
    if (wear === 2) {
      // The broken side of the crown: a notch of dark core and its stones at the foot.
      const a = Math.PI * 0.75;
      const notch = M(new THREE.BoxGeometry(0.26, 0.1, 0.06), stone('#3a3632'), g);
      notch.position.set(Math.cos(a) * 0.38, 1.75, Math.sin(a) * 0.38);
      notch.rotation.y = -a + Math.PI / 2;
    }
    rubbleAround(g, wear === 2 ? 11 : 5, 0.47, wr, ['#8b857a', '#7f796e', '#958e82']);
  }
  if (level >= 2) {
    // Iron cage over the fire.
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * Math.PI * 2;
      limb(g, V(Math.cos(a) * 0.21, 1.98, Math.sin(a) * 0.21), V(Math.cos(a) * 0.13, 2.4, Math.sin(a) * 0.13), 0.008, 0.008, DSTEEL, 4);
    }
    ring(g, 0.13, 0.012, DSTEEL, 2.4, 4, 14);
    ring(g, 0.31, 0.02, DSTEEL, 1.2, 4, 20);
  }
  if (level >= 3) {
    ring(g, 0.24, 0.02, BRASS, 2.0, 4, 18);
    for (const k of [-1, 1]) {
      const b = M(new THREE.BoxGeometry(0.18, 0.5, 0.01), cloth(ROYAL), g);
      b.position.set(k * 0.27, 1.35, 0.27);
      b.rotation.y = k * Math.PI / 4;
    }
  }
  if (level >= 4) {
    // A crown of iron spikes on the merlons and a taller cage: the fire burns higher than any other.
    for (let i = 0; i < 4; i++) {
      const a = (i / 4) * Math.PI * 2;
      if ((wear >= 1 && i === 1) || (wear === 2 && i === 2)) continue;
      M(new THREE.ConeGeometry(0.03, 0.14, 4), DSTEEL, g).position.set(Math.cos(a) * 0.36, 1.8 + mh + 0.07, Math.sin(a) * 0.36);
    }
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * Math.PI * 2 + Math.PI / 6;
      limb(g, V(Math.cos(a) * 0.2, 2.4, Math.sin(a) * 0.2), V(Math.cos(a) * 0.05, 2.62, Math.sin(a) * 0.05), 0.007, 0.007, DSTEEL, 4);
    }
    ring(g, 0.33, 0.025, DSTEEL, 0.55, 4, 20);
    glowSprite(g, '#ffb060', 0.9, V(0, 2.2, 0), 0.35);
  }
  // A sentinel keeps watch by the fire's tower.
  crewman(g, 0.36, 0, 0.42, 0.5, 'guard', 0.45);
  const t = (): void => {
    fl.flick();
    L.intensity = (1 + rng() * 0.4) * LIGHT_K;
  };
  return done(c, level >= 4 ? 2.65 : level >= 2 ? 2.45 : 2.3, t);
};

/* ---------- walls & gate ---------- */

export interface WallOpts extends BaseOpts {
  /** 1 palisade, 2 stone, 3 stone clad in iron (era II). */
  material?: 1 | 2 | 3;
  /** Gallery shorthand for wear 2. */
  damaged?: boolean;
  wear?: Wear;
}

/**
 * A single straight wall segment ~1 hex long (along X), centered.
 * Worn (1): one stake snapped, soot (wood); a merlon gone and cracks (stone); debris at the foot.
 * Crumbling (2): a breach of broken stakes and a leaning one (wood); the right half knocked down to a
 * stump with a block hanging off it (stone); much more debris.
 */
export const wall = (o?: WallOpts): Model => {
  const mat = o?.material ?? 2;
  const wear: Wear = o?.damaged ? 2 : (o?.wear ?? 0);
  const dmg = wear === 2;
  const c = begin(o, 0.75, 60 + mat + (dmg ? 5 : 0));
  const { g, R, rng } = c;
  const wr = mulberry32(600 + mat * 3 + wear);
  if (mat === 1) {
    const n = 9;
    const tip = WOOD;
    for (let i = 0; i < n; i++) {
      const x = -0.45 + (i / (n - 1)) * 0.9;
      if (dmg && i === 5) continue;
      let h = 0.85 + rng() * 0.12;
      const broken = (dmg && (i === 4 || i === 6)) || (wear === 1 && i === 6);
      if (broken) h *= wear === 1 ? 0.7 : 0.55;
      const log = M(new THREE.CylinderGeometry(0.055, 0.06, h, 6), WOOD, g);
      log.position.set(x, h / 2, 0);
      if (dmg && i === 2) { log.rotation.z = 0.18; log.position.x += 0.06; }
      if (!broken) {
        const cn = M(new THREE.ConeGeometry(0.055, 0.12, 6), tip, g);
        cn.position.set(log.position.x + (dmg && i === 2 ? 0.08 : 0), h + 0.06, 0);
        cn.rotation.z = log.rotation.z;
      } else {
        const sp = M(new THREE.ConeGeometry(0.035, 0.08, 4), DWOOD, g);
        sp.position.set(x + 0.02, h + 0.03, 0);
        sp.rotation.z = 0.4;
      }
    }
    for (const y of [0.25, 0.65]) M(new THREE.BoxGeometry(0.98, 0.06, 0.04), DWOOD, g).position.set(0, y, -0.07);
    // Walkway behind.
    M(new THREE.BoxGeometry(0.98, 0.04, 0.2), DWOOD, g).position.set(0, 0.55, -0.2);
    for (const x of [-0.4, 0.4]) limb(g, V(x, 0, -0.27), V(x, 0.55, -0.27), 0.025, 0.025, DWOOD, 5);
    if (dmg) for (let i = 0; i < 3; i++) {
      const p = M(new THREE.CylinderGeometry(0.05, 0.05, 0.3 + rng() * 0.2, 6), WOOD, g);
      p.rotation.set(Math.PI / 2, 0, R() * 2);
      p.position.set(0.1 + R() * 0.3, 0.05, 0.2 + R() * 0.1);
    }
    if (wear > 0) {
      // Fire-blackened logs on both faces.
      for (let i = 0; i < wear * 2; i++) for (const side of [1, -1]) stain(g, (wr() - 0.5) * 0.8, 0.25 + wr() * 0.45, side * 0.062, side > 0 ? 0 : Math.PI, 0.08 + wr() * 0.05, wr, '#2e241b');
      splinters(g, wear === 2 ? 8 : 4, 0.2, wr);
    }
    return done(c, 1.0);
  }
  const iron = mat === 3;
  const st = stone(iron ? IRON_STONE : '#857f74'), dark = std('#3a3632');
  const H = 0.66;
  if (!dmg) {
    M(new THREE.BoxGeometry(1.0, H, 0.35), st, g).position.y = H / 2;
  } else {
    M(new THREE.BoxGeometry(0.55, H, 0.35), st, g).position.set(-0.225, H / 2, 0);
    M(new THREE.BoxGeometry(0.45, H * 0.55, 0.35), st, g).position.set(0.275, (H * 0.55) / 2, 0);
    const chunk = M(new THREE.BoxGeometry(0.2, 0.18, 0.3), st, g);
    chunk.position.set(0.15, H * 0.55 + 0.06, 0.02);
    chunk.rotation.z = 0.35;
  }
  for (const y of [0.22, 0.44]) for (const z of [0.176, -0.176]) {
    const len = dmg && y > H * 0.5 ? 0.55 : 1.0;
    M(new THREE.BoxGeometry(len, 0.012, 0.004), dark, g).position.set(dmg && y > H * 0.5 ? -0.225 : 0, y, z);
  }
  for (let i = 0; i < 5; i++) {
    if ((dmg && i >= 3) || (wear === 1 && i === 1)) continue;
    const x = -0.4 + i * 0.2;
    M(new THREE.BoxGeometry(0.12, 0.16, 0.35), st, g).position.set(x, H + 0.08, 0);
  }
  if (dmg) {
    const rk = stone('#6d6559');
    for (let i = 0; i < 7; i++) M(new THREE.DodecahedronGeometry(0.04 + rng() * 0.05), rk, g).position.set(0.2 + R() * 0.5, 0.04, 0.22 + R() * 0.15);
  }
  if (iron) {
    // Riveted plates over the lower courses on both faces, a band under the parapet, capped merlons.
    for (const side of [1, -1]) {
      for (let i = 0; i < 3; i++) {
        const x = -0.33 + i * 0.33;
        if (dmg && x > 0.05) {
          // Torn loose: a plate hanging by a corner over the breach.
          if (i === 2) {
            const p = M(new THREE.BoxGeometry(0.3, 0.26, 0.014), IRON_PLATE, g);
            p.position.set(0.3, 0.16, side * 0.19);
            p.rotation.z = 0.5;
          }
          continue;
        }
        plate(g, x, 0.17, side * 0.183, 0.31, 0.27, side > 0 ? 0 : Math.PI);
      }
      M(new THREE.BoxGeometry(dmg ? 0.55 : 1.0, 0.04, 0.012), IRON_BAND, g).position.set(dmg ? -0.225 : 0, H - 0.08, side * 0.182);
    }
    for (let i = 0; i < 5; i++) {
      if ((dmg && i >= 3) || (wear === 1 && i === 1)) continue;
      M(new THREE.BoxGeometry(0.135, 0.025, 0.365), IRON_BAND, g).position.set(-0.4 + i * 0.2, H + 0.17, 0);
    }
  }
  if (wear > 0) {
    for (const side of [1, -1]) {
      for (let i = 0; i < wear + 1; i++) crack(g, -0.4 + wr() * (dmg ? 0.45 : 0.8), H - 0.05 - wr() * 0.15, side * 0.181, side > 0 ? 0 : Math.PI, dmg ? 0.36 : 0.24, wr);
      stain(g, (wr() - 0.5) * 0.8, 0.15 + wr() * 0.2, side * 0.179, side > 0 ? 0 : Math.PI, 0.08, wr);
    }
    fallen(g, wear === 2 ? 6 : 3, 0.9, 0.24, wr);
    fallen(g, wear === 2 ? 6 : 3, 0.9, -0.24, wr);
  }
  return done(c, H + 0.16);
};

/* ---------- bastion rampart (the ring around the keep) ---------- */

/**
 * The era V curtain's faces: two staggered rows of shell plates on each face (the table mostly sees the inner one),
 * a dashed line of pale runes between them. Worn walls have lost a few plates; over a crumbled gap the upper row is gone.
 */
const titanFaces = (
  g: THREE.Group, L: number, H: number, D: number, wear: Wear, rng: () => number, inGap: (a: number, b: number) => boolean, drop: number,
): void => {
  const r = 0.115;
  const step = r * 1.9;
  const n = Math.max(2, Math.floor(L / step));
  const pad = (L - n * step) / 2;
  for (const side of [1, -1]) {
    const z = side * (D / 2 + 0.008);
    const ry = side > 0 ? 0 : Math.PI;
    // The outer face (seldom seen from the table) keeps only its lower row.
    for (let row = 0; row < (side > 0 ? 1 : 2); row++) {
      const y = 0.1 + H * (row ? 0.74 : 0.28);
      const off = row ? step / 2 : 0;
      for (let i = 0; i < n - (row ? 1 : 0); i++) {
        const x = -L / 2 + pad + off + (i + 0.5) * step;
        if (row && inGap(x - r, x + r) && y > 0.1 + H - drop) continue;
        if (wear > 0 && rng() < (wear === 2 ? 0.2 : 0.1)) continue;
        shellPlate(g, x, y, z, r, ry, stone(TITAN_PLATES[(i + row + (side > 0 ? 0 : 1)) % 3] as string));
      }
    }
    // The runes: short dashes between the rows, a gap at every plate's corner.
    for (let i = 0; i < n; i++) {
      const x = -L / 2 + pad + (i + 0.5) * step;
      if (inGap(x - 0.06, x + 0.06)) continue;
      M(new THREE.BoxGeometry(step * 0.55, 0.014, 0.01), TITAN_RUNE, g).position.set(x, 0.1 + H * 0.51, z + side * 0.004);
    }
  }
};

/**
 * A curtain-wall run of the given length along X, outer face toward +Z: plinth, ashlar courses,
 * arrow slits, a crenellated parapet and a wall-walk behind it. Runs meet edge to edge at towers.
 * Worn (1): soot blotches, cracks, two merlons gone and stones at the foot. Crumbling (2): a stretch
 * of the parapet and the top courses broken away, wider cracks and a heap of fallen stone.
 * `titan` (era V, Somnhold): the ring is of the titan's stone: dark and cold, shell plates instead of ashlar on both
 * faces, a line of pale runes between them, slits lit from within and merlons capped like the shell's spurs.
 */
export const curtain = (o?: BaseOpts & { length?: number; wear?: Wear; titan?: boolean }): Model => {
  const L = o?.length ?? 1.2;
  const wear = o?.wear ?? 0;
  const titan = !!o?.titan;
  const c = begin(o, 0.75, 71 + Math.round(L * 10) + wear * 5);
  const { g, rng } = c;
  // Weathered stone darkens with wear, so the stage reads from the far table camera too.
  const body = stone(titan ? TITAN_WEAR[wear] : WEAR_STONE[wear]);
  const dark = stone(titan ? TITAN_DARK : '#6b655c');
  const H = 0.56;
  const D = 0.38;
  const top = 0.1 + H;
  // The broken stretch (crumbling only): [g0, g1] along X, the body there stops `drop` lower.
  const g0 = wear === 2 ? -L * 0.12 : 0;
  const g1 = wear === 2 ? L * 0.3 : 0;
  const drop = 0.24;
  const inGap = (a: number, b: number): boolean => wear === 2 && b > g0 && a < g1;
  // Battered plinth and body (split around the gap).
  M(new THREE.BoxGeometry(L, 0.1, D + 0.12), dark, g).position.y = 0.05;
  const slab = (a: number, b: number, h: number): void => {
    if (b - a < 0.005) return;
    M(new THREE.BoxGeometry(b - a, h, D), body, g).position.set((a + b) / 2, 0.1 + h / 2, 0);
  };
  if (wear === 2) {
    slab(-L / 2, g0, H);
    slab(g0, g1, H - drop);
    slab(g1, L / 2, H);
    // Jagged steps at both ends of the gap.
    for (const [x, dir] of [[g0, 1], [g1, -1]] as const) {
      for (let i = 0; i < 2; i++) {
        const w = 0.07 + rng() * 0.04;
        const h = drop * (i ? 0.4 : 0.75);
        const m = M(new THREE.BoxGeometry(w, h, D * (0.7 + rng() * 0.25)), body, g);
        m.position.set(x + dir * (i * 0.07 + w / 2), 0.1 + H - drop + h / 2, (rng() - 0.5) * 0.06);
      }
    }
  } else slab(-L / 2, L / 2, H);
  // Ashlar courses on the outer face, offset every other row (the titan's stone wears shell plates instead).
  const rows = titan ? 0 : 4;
  const bh = H / 4;
  if (titan) titanFaces(g, L, H, D, wear, rng, inGap, drop);
  for (let row = 0; row < rows; row++) {
    const n = Math.max(2, Math.round(L / 0.3));
    const w = L / n;
    const off = row % 2 ? w / 2 : 0;
    for (let i = -1; i < n; i++) {
      const x0 = -L / 2 + off + i * w;
      const x1 = Math.min(L / 2, x0 + w);
      const a = Math.max(-L / 2, x0);
      if (x1 - a < 0.04) continue;
      if (inGap(a, x1) && (row + 1) * bh > H - drop) continue;
      // A worn wall has lost a few facing stones; the core shows dark behind them.
      const lost = wear > 0 && rng() < (wear === 2 ? 0.16 : 0.08);
      const col = lost ? '#55504a' : (ASHLAR[Math.floor(rng() * ASHLAR.length)] as string);
      const blk = M(new THREE.BoxGeometry(x1 - a - 0.012, bh - 0.014, lost ? 0.008 : 0.025), stone(col), g);
      blk.position.set((a + x1) / 2, 0.1 + row * bh + bh / 2, D / 2 + (lost ? 0.002 : 0.01));
    }
  }
  // Arrow slits.
  for (const x of [-L / 4, L / 4]) if (!inGap(x - 0.02, x + 0.02)) M(new THREE.BoxGeometry(0.035, 0.16, 0.03), titan ? TITAN_RUNE : BLACK, g).position.set(x, 0.1 + H * 0.55, D / 2 + 0.02);
  // Parapet with merlons on the outer edge, wall-walk behind (both broken off over the gap).
  const spans: [number, number][] = wear === 2 ? [[-L / 2, g0], [g1, L / 2]] : [[-L / 2, L / 2]];
  for (const [a, b] of spans) {
    if (b - a < 0.02) continue;
    M(new THREE.BoxGeometry(b - a, 0.07, 0.12), body, g).position.set((a + b) / 2, top + 0.035, D / 2 - 0.06);
    M(new THREE.BoxGeometry(b - a, 0.025, D - 0.12), stone(titan ? '#4d535c' : '#7a7469'), g).position.set((a + b) / 2, top + 0.012, -0.06);
  }
  const merlons = Math.max(2, Math.round(L / 0.2));
  // Worn: two merlons knocked off (one on a half run).
  const missing = new Set<number>();
  if (wear > 0) for (let k = 0; k < (merlons > 3 ? 2 : 1) + (wear === 2 ? 1 : 0); k++) missing.add(Math.floor(rng() * merlons));
  for (let i = 0; i < merlons; i++) {
    const x = -L / 2 + (i + 0.5) * (L / merlons);
    const mw = (L / merlons) * 0.55;
    if (missing.has(i) || inGap(x - mw / 2, x + mw / 2)) continue;
    M(new THREE.BoxGeometry(mw, 0.13, 0.12), body, g).position.set(x, top + 0.07 + 0.065, D / 2 - 0.06);
    if (titan) {
      const spur = M(new THREE.ConeGeometry(Math.min(mw, 0.12) * 0.62, 0.09, 4), stone(TITAN_DARK), g);
      spur.position.set(x, top + 0.2 + 0.045, D / 2 - 0.06);
      spur.rotation.y = Math.PI / 4;
    }
  }
  if (wear > 0) {
    // Soot and damp blotches and cracks on both faces (the table mostly sees the inner one).
    const blot = stone('#5d584f');
    for (const side of [1, -1]) {
      const z = side * (D / 2 + (side > 0 ? 0.024 : 0.003));
      for (let i = 0; i < (wear === 2 ? 4 : 3); i++) {
        const s = 0.07 + rng() * 0.07;
        const m = M(new THREE.CircleGeometry(s, 7), blot, g);
        m.scale.y = 0.6 + rng() * 0.5;
        if (side < 0) m.rotation.y = Math.PI;
        m.position.set((rng() - 0.5) * L * 0.85, 0.2 + rng() * (H - 0.3), z);
      }
      for (let i = 0; i < (wear === 2 ? 3 : 2); i++) {
        const x = (rng() - 0.5) * L * 0.8;
        if (inGap(x - 0.05, x + 0.05)) continue;
        crack(g, x, 0.12 + H * (0.75 + rng() * 0.2), z + side * 0.002, side > 0 ? 0 : Math.PI, wear === 2 ? 0.34 : 0.22, rng);
      }
      fallen(g, wear === 2 ? 7 : 3, L * 0.9, side * (D / 2 + 0.06), rng);
    }
    if (wear === 2) {
      // The heap below the gap, spilling to both sides.
      const heap = M(new THREE.CylinderGeometry(0.2, 0.26, 0.08, 7), stone('#6b655c'), g);
      heap.scale.set(Math.max(1, (g1 - g0) / 0.4), 1, 1.6);
      heap.position.set((g0 + g1) / 2, 0.04, 0);
      for (let i = 0; i < 5; i++) {
        const blk = M(new THREE.BoxGeometry(0.15, 0.08, 0.11), body, g);
        blk.position.set(g0 + rng() * (g1 - g0), 0.1 + H - drop + 0.04 + (i % 2) * 0.03, (rng() - 0.5) * D * 0.7);
        blk.rotation.set((rng() - 0.5) * 0.6, rng() * Math.PI, (rng() - 0.5) * 0.6);
      }
    }
  } else if (!titan) {
    // A little moss and fallen stone at the foot.
    const moss = std('#4f6b3a', { roughness: 1 });
    for (let i = 0; i < 3; i++) {
      const m = M(new THREE.SphereGeometry(0.06 + rng() * 0.04, 6, 4), moss, g);
      m.scale.y = 0.35;
      m.position.set((rng() - 0.5) * L * 0.9, 0.04, D / 2 + 0.07);
    }
  }
  return done(c, top + 0.2);
};

/**
 * A ring tower where the rampart turns (big: round, slate roof and pennant) or a buttress on straight runs.
 * Worn (1): cracks and a few lost crenels. Crumbling (2): the crown knocked off (the big tower loses its
 * roof and pennant and stands lower and jagged) and stones heaped at its foot.
 */
/**
 * A tower of the ring wall. `vigil` (era IV on, the Vigil's bastion): the Order keeps watch from the ring again: the
 * big towers hang its long crimson standard outward and wear a silver finial lit white; the small ones burn the
 * Vigil's silver-white flame in a brazier on top.
 */
export const rampartTower = (o?: BaseOpts & { big?: boolean; wear?: Wear; vigil?: boolean; titan?: boolean }): Model => {
  const big = !!o?.big;
  const titan = !!o?.titan;
  // Somnhold's ring keeps the Order's watch.
  const vigil = !!o?.vigil || titan;
  const wear = o?.wear ?? 0;
  const c = begin(o, 0.6, (big ? 81 : 82) + wear * 7);
  const { g, rng } = c;
  const body = stone(titan ? TITAN_WEAR[wear] : WEAR_STONE[wear]);
  const dark = stone(titan ? TITAN_DARK : '#6b655c');
  const scars = (r: number, y0: number, h: number): void => {
    for (let i = 0; i < 2 * wear + 1; i++) {
      const a = (i / (2 * wear + 1)) * Math.PI * 2 + rng();
      crack(g, Math.sin(a) * (r + 0.01), y0 + h * (0.6 + rng() * 0.3), Math.cos(a) * (r + 0.01), a, wear === 2 ? 0.32 : 0.22, rng);
    }
  };
  if (big) {
    const bodyH = wear === 2 ? 0.66 : 0.9;
    M(new THREE.CylinderGeometry(0.42, 0.46, 0.12, 12), dark, g).position.y = 0.06;
    M(new THREE.CylinderGeometry(0.36, 0.41, bodyH, 12), body, g).position.y = 0.12 + bodyH / 2;
    if (titan) titanDrum(g, 0.36, 0.41, 0.12, bodyH, 12, wear, rng);
    else {
      const mortar = std('#5f5a52');
      for (let i = 1; i < 5; i++) if (0.12 + i * 0.18 < 0.12 + bodyH - 0.05) ring(g, 0.39 - i * 0.008, 0.005, mortar, 0.12 + i * 0.18, 3, 24);
    }
    for (const a of [0.3, 2.4, 4.5]) M(new THREE.BoxGeometry(0.035, 0.15, 0.03), titan ? TITAN_RUNE : BLACK, g).position.set(Math.cos(a) * 0.37, wear === 2 ? 0.55 : 0.75, Math.sin(a) * 0.37);
    if (wear === 2) {
      // Broken crown: a few jagged stumps of the old parapet around the open top.
      for (let i = 0; i < 6; i++) {
        const a = (i / 6) * Math.PI * 2 + rng() * 0.3;
        const h = 0.08 + rng() * 0.16;
        const m = M(new THREE.BoxGeometry(0.13, h, 0.09), body, g);
        m.position.set(Math.cos(a) * 0.34, 0.12 + bodyH + h / 2, Math.sin(a) * 0.34);
        m.rotation.y = -a;
      }
      const hollow = M(new THREE.CircleGeometry(0.32, 12), stone('#3d3934'), g);
      hollow.rotation.x = -Math.PI / 2;
      hollow.position.y = 0.12 + bodyH + 0.008;
      scars(0.39, 0.12, bodyH);
      fallen(g, 7, 0.9, 0.42, rng);
      fallen(g, 5, 0.9, -0.42, rng);
      return done(c, 0.12 + bodyH + 0.3);
    }
    M(new THREE.CylinderGeometry(0.42, 0.37, 0.1, 12), body, g).position.y = 1.07;
    for (let i = 0; i < 10; i++) {
      if (wear === 1 && (i === 2 || i === 3 || i === 7)) continue;
      const a = (i / 10) * Math.PI * 2;
      const m = M(new THREE.BoxGeometry(0.11, 0.13, 0.08), body, g);
      m.position.set(Math.cos(a) * 0.38, 1.18, Math.sin(a) * 0.38);
      m.rotation.y = -a;
    }
    // Somnhold's towers: a taller, darker roof with a ring of pale runes at its eaves.
    const roof = M(new THREE.ConeGeometry(0.36, titan ? 0.72 : 0.55, 12), stone(titan ? '#2b2f36' : '#3f4450'), g);
    roof.position.y = titan ? 1.48 : 1.4;
    if (titan) ring(g, 0.33, 0.012, TITAN_RUNE, 1.16, 3, 24);
    banner(g, 0, titan ? 1.76 : 1.6, 0, 0.45, ROYAL, 0.18);
    if (vigil) {
      // The Order's standard down the outer face, a silver finial lit white on the roof.
      M(new THREE.BoxGeometry(0.2, 0.62, 0.014), cloth('#7a1d22'), g).position.set(0, 0.68, 0.425);
      const em = M(new THREE.BoxGeometry(0.07, 0.07, 0.018), cloth('#d9dce3'), g);
      em.position.set(0, 0.86, 0.433);
      em.rotation.z = Math.PI / 4;
      M(new THREE.BoxGeometry(0.22, 0.025, 0.03), DWOOD, g).position.set(0, 1.0, 0.43);
      if (titan) {
        // A shard of the Dream Beacon's light on the spire, in a silver collar.
        M(new THREE.TorusGeometry(0.05, 0.01, 4, 10), SILVER, g).position.y = 1.86;
        const shard = M(new THREE.OctahedronGeometry(0.06, 0), DREAM_LENS, g);
        shard.position.y = 1.92;
        shard.scale.y = 1.6;
        glowSprite(g, '#cfe4ff', 0.8, V(0, 1.92, 0), 0.75);
      } else {
        M(new THREE.SphereGeometry(0.035, 8, 6), SILVER, g).position.y = 1.7;
        glowSprite(g, '#dfe8ff', 0.5, V(0, 1.72, 0), 0.6);
      }
    }
    if (wear === 1) {
      scars(0.39, 0.12, 0.9);
      fallen(g, 3, 0.7, 0.42, rng);
      fallen(g, 2, 0.7, -0.42, rng);
    }
    return done(c, titan ? 2.2 : 2.1);
  }
  const bodyH = wear === 2 ? 0.5 : 0.66;
  M(new THREE.CylinderGeometry(0.3, 0.33, 0.1, 8), dark, g).position.y = 0.05;
  M(new THREE.CylinderGeometry(0.25, 0.29, bodyH, 8), body, g).position.y = 0.1 + bodyH / 2;
  if (titan) titanDrum(g, 0.25, 0.29, 0.1, bodyH, 8, wear, rng, 1);
  if (wear === 2) {
    for (let i = 0; i < 3; i++) {
      const a = (i / 3) * Math.PI * 2 + rng();
      const h = 0.06 + rng() * 0.12;
      const m = M(new THREE.BoxGeometry(0.12, h, 0.09), body, g);
      m.position.set(Math.cos(a) * 0.22, 0.1 + bodyH + h / 2, Math.sin(a) * 0.22);
      m.rotation.y = -a;
    }
    scars(0.28, 0.1, bodyH);
    fallen(g, 4, 0.7, 0.3, rng);
    fallen(g, 4, 0.7, -0.3, rng);
    return done(c, 0.1 + bodyH + 0.2);
  }
  M(new THREE.CylinderGeometry(0.3, 0.26, 0.08, 8), body, g).position.y = 0.8;
  for (let i = 0; i < 4; i++) {
    if (wear === 1 && i === 1) continue;
    const a = (i / 4) * Math.PI * 2 + Math.PI / 4;
    const m = M(new THREE.BoxGeometry(0.12, 0.13, 0.09), body, g);
    m.position.set(Math.cos(a) * 0.24, 0.9, Math.sin(a) * 0.24);
    m.rotation.y = -a;
  }
  if (titan) {
    // Somnhold's watch-fire: a drum of the titan's stone holding a shard of the Dream Beacon, its flame blue-white and tall.
    M(new THREE.CylinderGeometry(0.11, 0.09, 0.1, 8), stone(TITAN_DARK), g).position.y = 0.89;
    ring(g, 0.105, 0.01, TITAN_RUNE, 0.9, 3, 10);
    flame(g, V(0, 0.95, 0), 0.85, rng, '#cfe4ff');
    const shard = M(new THREE.OctahedronGeometry(0.055, 0), DREAM_LENS, g);
    shard.position.y = 1.16;
    shard.scale.y = 1.7;
    glowSprite(g, '#cfe4ff', 1.0, V(0, 1.08, 0), 0.8);
  } else if (vigil) {
    // The Vigil's flame in an iron brazier on top: silver-white.
    for (let i = 0; i < 3; i++) {
      const a = (i / 3) * Math.PI * 2;
      limb(g, V(Math.cos(a) * 0.1, 0.84, Math.sin(a) * 0.1), V(Math.cos(a) * 0.05, 0.98, Math.sin(a) * 0.05), 0.01, 0.012, DSTEEL, 4);
    }
    const bowl = M(new THREE.SphereGeometry(0.08, 8, 4, 0, Math.PI * 2, Math.PI / 2, Math.PI / 2), metal('#3a3a40', { side: THREE.DoubleSide }), g);
    bowl.position.y = 1.02;
    flame(g, V(0, 1.02, 0), 0.6, rng, '#dfe8ff');
    glowSprite(g, '#cfdcff', 0.7, V(0, 1.12, 0), 0.7);
  }
  if (wear === 1) {
    scars(0.28, 0.1, 0.66);
    fallen(g, 2, 0.5, 0.3, rng);
    fallen(g, 2, 0.5, -0.3, rng);
  }
  return done(c, titan ? 1.3 : vigil ? 1.2 : 1.0);
};

/** Shell plates round a tower's drum (radius rTop at y0 + h, rBot at y0) in two staggered rings, a ring of pale runes between. */
const titanDrum = (g: THREE.Group, rTop: number, rBot: number, y0: number, h: number, sides: number, wear: Wear, rng: () => number, rows = 2): void => {
  const rAt = (y: number): number => rBot + ((rTop - rBot) * (y - y0)) / h;
  const n = sides;
  const r = Math.min(0.11, rAt(y0 + h / 2) * Math.sin(Math.PI / n) * 1.05);
  for (let row = 0; row < rows; row++) {
    const y = y0 + h * (row ? 0.72 : 0.3);
    if (y + r > y0 + h) continue;
    for (let i = 0; i < n; i++) {
      if (wear > 0 && rng() < (wear === 2 ? 0.25 : 0.12)) continue;
      const a = ((i + (row ? 0.5 : 0)) / n) * Math.PI * 2;
      const d = rAt(y) * Math.cos(Math.PI / n) + 0.006;
      shellPlate(g, Math.sin(a) * d, y, Math.cos(a) * d, r, a, stone(TITAN_PLATES[(i + row) % 3] as string), 0.02);
    }
  }
  if (y0 + h * 0.52 < y0 + h - 0.02) ring(g, rAt(y0 + h * 0.52) + 0.004, 0.008, TITAN_RUNE, y0 + h * 0.52, 3, sides * 2);
};

/**
 * A breach in the rampart: the run's stones spilled across the cell, a low mound along X (the line of
 * the wall) that leans outward (+Z), where the blow came from. Two broken stumps mark where it stood.
 */
export const rampartRubble = (o?: BaseOpts): Model => {
  const c = begin(o, 0.6, 83);
  const { g, rng, R } = c;
  const dark = stone('#6b655c');
  const body = stone('#8f887c');
  const grit = stone('#7a7469');
  const mound = M(new THREE.CylinderGeometry(0.5, 0.6, 0.12, 9), dark, g);
  mound.scale.z = 0.55;
  mound.position.set(0, 0.06, 0.05);
  for (const x of [-0.46, 0.46]) {
    const h = 0.16 + rng() * 0.12;
    const stump = M(new THREE.BoxGeometry(0.2, h, 0.34), body, g);
    stump.position.set(x, 0.1 + h / 2, -0.02);
    stump.rotation.y = R() * 0.3;
  }
  // Dressed blocks, some still square, tumbled over the mound.
  for (let i = 0; i < 9; i++) {
    const blk = M(new THREE.BoxGeometry(0.16 + rng() * 0.08, 0.09, 0.11 + rng() * 0.05), stone(ASHLAR[Math.floor(rng() * ASHLAR.length)] as string), g);
    blk.position.set(R() * 0.8, 0.13 + rng() * 0.08, R() * 0.3 + 0.06);
    blk.rotation.set(R() * 0.7, rng() * Math.PI, R() * 0.7);
  }
  // Broken chunks and grit thrown further out.
  for (let i = 0; i < 8; i++) {
    const s = 0.04 + rng() * 0.06;
    M(new THREE.DodecahedronGeometry(s, 0), grit, g).position.set(R() * 1.0, s * 0.6, 0.2 + rng() * 0.25);
  }
  return done(c, 0.35);
};

export interface GateOpts extends BaseOpts { material?: 1 | 2 | 3; open?: number; wear?: Wear }
export interface GateModel extends Model { setOpen(v: number): void }

/**
 * Gatehouse, one hex wide (1.2 world units at scale 1) so it closes the rampart gap exactly: two flanking
 * towers, an arch with a parapet and a portcullis (part 'portcullis', moves up on Y as it opens).
 * Stone is crenellated like the rampart; wood is a log palisade with pyramid roofs.
 */
export const gate = (o?: GateOpts): GateModel => {
  const mat = o?.material ?? 2;
  const wear = o?.wear ?? 0;
  const c = begin(o, 0.75, 66 + mat);
  const { g, rng } = c;
  const wr = mulberry32(660 + mat * 3 + wear);
  const W = 1.2;
  const D = 0.5;
  const tw = 0.36;
  const th = 0.98;
  const tx = W / 2 - tw / 2;
  const archTop = 0.86;
  const openH = 0.58;
  const openW = W - 2 * tw;
  if (mat >= 2) {
    const iron = mat === 3;
    const body = stone(iron ? IRON_STONE : '#8f887c');
    const dark = stone(iron ? '#55595f' : '#6b655c');
    M(new THREE.BoxGeometry(W, 0.1, D + 0.1), dark, g).position.y = 0.05;
    for (const x of [-tx, tx]) {
      // Crumbling: the right tower is broken down to a jagged stump below its crown.
      const ruined = wear === 2 && x > 0;
      const h = ruined ? th * 0.7 : th;
      M(new THREE.BoxGeometry(tw, h, D), body, g).position.set(x, h / 2, 0);
      if (ruined) {
        for (let i = 0; i < 4; i++) {
          const sh = 0.05 + wr() * 0.12;
          const m = M(new THREE.BoxGeometry(0.1 + wr() * 0.06, sh, 0.12 + wr() * 0.1), body, g);
          m.position.set(x + (wr() - 0.5) * (tw - 0.1), h + sh / 2, (wr() - 0.5) * (D - 0.15));
          m.rotation.y = (wr() - 0.5) * 0.4;
        }
      } else {
        M(new THREE.BoxGeometry(tw + 0.04, 0.06, D + 0.04), body, g).position.set(x, th + 0.03, 0);
        let k = 0;
        for (const dx of [-0.11, 0.11]) for (const dz of [-0.17, 0.17]) {
          // Worn: one merlon knocked off each tower.
          if (wear >= 1 && k++ === (x < 0 ? 1 : 2)) continue;
          M(new THREE.BoxGeometry(0.1, 0.13, 0.1), body, g).position.set(x + dx, th + 0.12, dz);
        }
      }
      for (let row = 0; row < 5; row++) {
        const col = stone(ASHLAR[Math.floor(rng() * ASHLAR.length)] as string);
        if (0.14 + row * 0.17 + 0.075 > h) continue;
        const blk = M(new THREE.BoxGeometry(tw - 0.03, 0.15, 0.02), col, g);
        blk.position.set(x, 0.14 + row * 0.17, D / 2 + 0.01);
      }
      M(new THREE.BoxGeometry(0.035, 0.17, 0.03), BLACK, g).position.set(x, 0.62, D / 2 + 0.025);
      if (iron) {
        // Iron-clad towers: a riveted skirt on both faces, girdles at two heights, angle irons on the corners.
        for (const side of [1, -1]) plate(g, x, 0.25, side * (D / 2 + 0.024), tw - 0.04, 0.32, side > 0 ? 0 : Math.PI);
        for (const y of [0.47, Math.min(h - 0.06, 0.86)]) M(new THREE.BoxGeometry(tw + 0.02, 0.04, D + 0.02), IRON_BAND, g).position.set(x, y, 0);
        for (const cx of [-1, 1]) for (const cz of [-1, 1]) M(new THREE.BoxGeometry(0.03, h - 0.04, 0.03), IRON_BAND, g).position.set(x + cx * (tw / 2), h / 2, cz * (D / 2));
      }
      if (wear > 0) for (const side of [1, -1]) {
        crack(g, x + (wr() - 0.5) * 0.2, h - 0.08, side * (D / 2 + 0.024), side > 0 ? 0 : Math.PI, wear === 2 ? 0.45 : 0.26, wr);
        // Low on the outer edge of the tower, well clear of the arrow slit.
        stain(g, x + Math.sign(x) * 0.1, 0.2 + wr() * 0.06, side * (D / 2 + 0.023), side > 0 ? 0 : Math.PI, 0.06 + wr() * 0.03, wr);
      }
    }
    M(new THREE.BoxGeometry(openW, archTop - openH, D - 0.04), body, g).position.set(0, openH + (archTop - openH) / 2, 0);
    if (wear < 2) for (const x of [-0.12, 0.12]) M(new THREE.BoxGeometry(0.1, 0.12, 0.1), body, g).position.set(x, archTop + 0.06, D / 2 - 0.06);
    // Keystone over the arch (crumbling: it has dropped to the ground).
    const key = M(new THREE.BoxGeometry(0.1, 0.12, 0.03), stone('#a39c90'), g);
    if (wear === 2) {
      key.position.set(0.12, 0.03, D / 2 + 0.2);
      key.rotation.set(Math.PI / 2, 0, 0.6);
      M(new THREE.BoxGeometry(0.1, 0.12, 0.012), stone('#3a3632'), g).position.set(0, openH + 0.04, D / 2 - 0.004);
    } else key.position.set(0, openH + 0.04, D / 2);
    if (wear > 0) {
      fallen(g, wear === 2 ? 8 : 3, W, D / 2 + 0.06, wr);
      fallen(g, wear === 2 ? 8 : 3, W, -D / 2 - 0.06, wr);
    }
  } else {
    const plank = std('#6b4a2e', { roughness: 0.95, flatShading: true, bumpScale: 0.03 });
    const roofM = std('#3a271a', { roughness: 0.95, flatShading: true });
    for (const x of [-tx, tx]) {
      const ruined = wear === 2 && x > 0;
      M(new THREE.BoxGeometry(tw, th - 0.12, D), plank, g).position.set(x, (th - 0.12) / 2, 0);
      // Log facing on both sides (the table mostly sees the inner one), with iron bands.
      for (const side of [1, -1]) {
        for (let i = 0; i < 4; i++) {
          const lx = x - tw / 2 + 0.045 + i * ((tw - 0.09) / 3);
          let h = th - 0.16 + rng() * 0.05;
          // Snapped logs: one per side when worn, most of the ruined tower's when crumbling.
          const snap = (wear >= 1 && x < 0 && side > 0 && i === 2) || (ruined && i !== 0);
          if (snap) h *= 0.55 + wr() * 0.2;
          M(new THREE.CylinderGeometry(0.045, 0.05, h, 6), WOOD, g).position.set(lx, h / 2, side * (D / 2 + 0.02));
          if (snap) snapTop(g, lx, h, side * (D / 2 + 0.02), 0.045, WOOD, wr);
        }
        for (const y of [0.22, 0.55]) {
          const band = M(new THREE.BoxGeometry(tw + 0.02, 0.04, 0.02), DSTEEL, g);
          band.position.set(x, y, side * (D / 2 + 0.07));
          // The upper band of the ruined tower hangs loose from one nail.
          if (ruined && y > 0.5) {
            band.rotation.z = -0.55;
            band.position.set(x + 0.03, y - 0.1, side * (D / 2 + 0.08));
          }
        }
        if (wear > 0) stain(g, x + (wr() - 0.5) * 0.2, 0.3 + wr() * 0.3, side * (D / 2 + 0.075), side > 0 ? 0 : Math.PI, 0.08 + wr() * 0.05, wr, '#2e241b');
      }
      // Low pyramid roof, aligned with the tower (crumbling: the right one has fallen beside it).
      const roof = M(new THREE.ConeGeometry((tw + 0.04) * Math.SQRT1_2, 0.18, 4), roofM, g);
      roof.rotation.y = Math.PI / 4;
      roof.scale.z = (D + 0.04) / (tw + 0.04);
      roof.position.set(x, th - 0.12 + 0.09, 0);
      if (ruined) {
        roof.position.set(x + 0.32, 0.1, D / 2 + 0.12);
        roof.rotation.set(0.5, Math.PI / 4, 1.1);
      }
    }
    if (wear > 0) {
      splinters(g, wear === 2 ? 9 : 4, 0.35, wr, 0, D / 2 + 0.1);
      splinters(g, wear === 2 ? 6 : 3, 0.35, wr, 0, -D / 2 - 0.1);
    }
    M(new THREE.BoxGeometry(openW + 0.02, archTop - openH, D - 0.06), plank, g).position.set(0, openH + (archTop - openH) / 2, 0);
    for (let i = 0; i < 5; i++) {
      const lx = -openW / 2 + 0.05 + i * ((openW - 0.1) / 4);
      M(new THREE.ConeGeometry(0.04, 0.1, 6), WOOD, g).position.set(lx, archTop + 0.05, D / 2 - 0.08);
    }
  }
  M(new THREE.BoxGeometry(openW - 0.02, openH, 0.05), BLACK, g).position.set(0, openH / 2, -0.06);
  const port = part(G(g), 'portcullis');
  const barM = mat >= 2 ? DSTEEL : DWOOD;
  const br = mat >= 2 ? 0.013 : 0.022;
  const n = 5;
  const half = openW / 2 - 0.05;
  for (let i = 0; i < n; i++) {
    const x = -half + (i * 2 * half) / (n - 1);
    limb(port, V(x, 0, 0.14), V(x, openH - 0.02, 0.14), br, br, barM, 5);
    const sp = M(new THREE.ConeGeometry(0.016, 0.05, 4), DSTEEL, port);
    sp.position.set(x, -0.02, 0.14);
    sp.rotation.x = Math.PI;
  }
  for (let j = 0; j < 3; j++) limb(port, V(-half - 0.02, 0.1 + j * 0.18, 0.14), V(half + 0.02, 0.1 + j * 0.18, 0.14), br * 0.85, br * 0.85, barM, 5);
  const setOpen = (v: number): void => { port.position.y = Math.max(0, Math.min(1, v)) * (openH - 0.08); };
  setOpen(o?.open ?? 0);
  return { ...done(c, th + 0.2), setOpen };
};

/* ---------- ruins (destroyed structures) ---------- */

export interface RuinOpts extends BaseOpts {
  /** Structure type the ruin is left by (its footprint and leftovers). */
  type?: string;
  /** 1 wood, 2 stone, for the types built in either. */
  material?: 1 | 2;
}

/**
 * What a destroyed structure leaves, in its own footprint and material: a jagged stump of stone or a ring
 * of snapped stakes over a scorched patch, the rubble around it and one telling leftover (the bell, the
 * anvil, the tipped brazier, the fallen portcullis, a dim crystal shard).
 */
export const ruin = (o?: RuinOpts): Model => {
  const type = o?.type ?? 'wall';
  const mat = o?.material ?? 2;
  const c = begin(o, 0.7, 90);
  const { g } = c;
  const rng = mulberry32(900 + type.length * 13 + type.charCodeAt(0) + mat * 7);
  const charred = std('#2b2119', { roughness: 1 });
  const scorch = (r: number, x = 0, z = 0, sx = 1): void => {
    const d = M(new THREE.CircleGeometry(r, 12), std('#1e1a16', { roughness: 1, bumpScale: 0 }), g);
    d.rotation.x = -Math.PI / 2;
    d.scale.x = sx;
    d.position.set(x, 0.006, z);
  };
  /** A broken round or square stump with jagged stubs around its rim and the dark core showing on top. */
  const stump = (r0: number, r1: number, h: number, sides: number, color: string, x = 0, z = 0): void => {
    const body = stone(color);
    const s = M(new THREE.CylinderGeometry(r0, r1, h, sides), body, g);
    s.position.set(x, h / 2, z);
    if (sides === 4) s.rotation.y = Math.PI / 4;
    const core = M(new THREE.CircleGeometry(r0 * 0.85, sides < 6 ? 4 : 10), stone('#34302b'), g);
    core.rotation.x = -Math.PI / 2;
    if (sides === 4) core.rotation.z = Math.PI / 4;
    core.position.set(x, h + 0.003, z);
    const n = sides === 4 ? 6 : 7;
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2 + rng() * 0.5;
      const sh = 0.05 + rng() * 0.18;
      const rr = sides === 4 ? r0 * 0.62 : r0 * 0.82;
      const m = M(new THREE.BoxGeometry(0.1 + rng() * 0.06, sh, 0.09 + rng() * 0.05), body, g);
      m.position.set(x + Math.sin(a) * rr, h + sh / 2 - 0.01, z + Math.cos(a) * rr);
      m.rotation.set((rng() - 0.5) * 0.25, a, (rng() - 0.5) * 0.25);
    }
  };
  /** Snapped stakes in a ring (wooden towers) or a row along X (palisades). */
  const stakes = (pts: [number, number][], r: number): void => {
    for (const [x, z] of pts) {
      const h = 0.08 + rng() * 0.3;
      M(new THREE.CylinderGeometry(r, r * 1.1, h, 6), rng() < 0.4 ? charred : WOOD, g).position.set(x, h / 2, z);
      snapTop(g, x, h, z, r, WOOD, rng);
    }
  };
  const log = (x: number, z: number, len: number, ry: number, r = 0.055): void => {
    const m = M(new THREE.CylinderGeometry(r, r, len, 6), rng() < 0.5 ? charred : WOOD, g);
    m.rotation.set(Math.PI / 2, 0, 0);
    const p = G(g, x, r, z);
    p.rotation.y = ry;
    p.add(m);
  };
  let h = 0.45;
  switch (type) {
    case 'ballista': {
      if (mat === 1) {
        scorch(0.6);
        stakes(Array.from({ length: 12 }, (_, i) => [Math.cos((i / 12) * Math.PI * 2) * 0.42, Math.sin((i / 12) * Math.PI * 2) * 0.42] as [number, number]), 0.06);
        for (let i = 0; i < 4; i++) log((rng() - 0.5) * 0.5, (rng() - 0.5) * 0.5, 0.6 + rng() * 0.4, rng() * Math.PI);
        splinters(g, 10, 0.45, rng);
      } else {
        stump(0.46, 0.52, 0.32, 10, '#8b857a');
        rubbleAround(g, 16, 0.5, rng);
        // The engine's beam, thrown across the rubble.
        const beam = M(new THREE.BoxGeometry(0.08, 0.08, 0.6), DWOOD, g);
        beam.position.set(0.35, 0.12, 0.45);
        beam.rotation.set(0.25, 0.8, 0);
      }
      break;
    }
    case 'catapult': {
      scorch(0.55, 0, 0, 1.3);
      const bed = M(new THREE.BoxGeometry(0.9, 0.08, 0.55), WOOD, g);
      bed.position.set(0, 0.12, 0);
      bed.rotation.set(0.05, 0.2, 0.14);
      for (const [x, z, up] of [[-0.62, 0.3, 0], [0.5, -0.5, 0], [-0.3, -0.42, 1]] as const) {
        const w = M(new THREE.CylinderGeometry(0.13, 0.13, 0.06, 10), DWOOD, g);
        w.position.set(x, up ? 0.12 : 0.03, z);
        if (up) w.rotation.set(Math.PI / 2 - 0.4, 0, 0);
      }
      // The arm, broken, lying over the bed with its cup and counterweight.
      log(0.15, 0.1, 0.85, 0.5, 0.025);
      M(new THREE.SphereGeometry(0.1, 8, 5, 0, Math.PI * 2, 0, Math.PI / 2), DWOOD, g).position.set(0.5, 0.02, 0.32);
      M(new THREE.BoxGeometry(0.18, 0.18, 0.18), stone('#4f4a44'), g).position.set(-0.45, 0.09, -0.15);
      splinters(g, 10, 0.5, rng);
      h = 0.35;
      break;
    }
    case 'belfry': {
      stump(0.42, 0.44, 0.42, 4, '#857f74');
      rubbleAround(g, 14, 0.42, rng, ['#857f74', '#7a746a', '#8f887c']);
      // The bell, fallen on its side.
      const bell = M(new THREE.CylinderGeometry(0.09, 0.19, 0.26, 12, 1, true), metal('#9a6a2a', { side: THREE.DoubleSide, roughness: 0.4 }), g);
      bell.position.set(0.5, 0.16, 0.38);
      bell.rotation.set(Math.PI / 2 - 0.25, 0.7, 0);
      for (let i = 0; i < 4; i++) {
        const sl = M(new THREE.BoxGeometry(0.16, 0.025, 0.11), stone('#3e3a36'), g);
        sl.position.set(-0.5 + rng() * 0.3, 0.02, -0.2 + rng() * 0.5);
        sl.rotation.set(rng() * 0.3, rng() * Math.PI, 0);
      }
      break;
    }
    case 'arcane': {
      stump(0.36, 0.45, 0.42, 8, '#4d4a55');
      rubbleAround(g, 14, 0.48, rng, ['#4d4a55', '#5a5762', '#423f49']);
      const cm = emis('#7fa8bf', '#3aa6ff', 0.35, { flatShading: true, roughness: 0.1 });
      for (const [x, z, rz] of [[0.5, 0.3, 1.2], [-0.42, 0.45, 1.5], [0.1, -0.55, 1.0]] as const) {
        const sh = M(new THREE.OctahedronGeometry(0.08), cm, g);
        sh.scale.y = 2;
        sh.position.set(x, 0.07, z);
        sh.rotation.z = rz;
      }
      glowSprite(g, '#3aa6ff', 0.25, V(0.5, 0.1, 0.3), 0.45);
      break;
    }
    case 'beacon': {
      stump(0.34, 0.42, 0.55, 10, '#8b857a');
      rubbleAround(g, 14, 0.48, rng, ['#8b857a', '#7f796e', '#958e82']);
      // The brazier tipped over, ash and a few live embers spilled from it.
      const pan = M(new THREE.CylinderGeometry(0.22, 0.14, 0.18, 10, 1, true), metal('#3a3d42', { side: THREE.DoubleSide, roughness: 0.5 }), g);
      pan.position.set(0.48, 0.18, 0.35);
      pan.rotation.set(Math.PI / 2 - 0.3, 0.6, 0);
      scorch(0.22, 0.7, 0.55);
      const ember = emis('#ff6a1a', '#ff3a00', 0.8);
      for (let i = 0; i < 4; i++) M(new THREE.DodecahedronGeometry(0.025), ember, g).position.set(0.62 + rng() * 0.2, 0.02, 0.5 + rng() * 0.15);
      h = 0.6;
      break;
    }
    case 'wall': {
      if (mat === 1) {
        scorch(0.5, 0, 0, 1.4);
        stakes(Array.from({ length: 9 }, (_, i) => [-0.45 + (i / 8) * 0.9, 0] as [number, number]), 0.055);
        for (let i = 0; i < 3; i++) log(-0.3 + i * 0.3, 0.2 + rng() * 0.1, 0.6 + rng() * 0.3, Math.PI / 2 + (rng() - 0.5) * 0.6);
        splinters(g, 8, 0.25, rng);
      } else {
        const body = stone('#857f74');
        M(new THREE.BoxGeometry(1.0, 0.18, 0.35), body, g).position.y = 0.09;
        for (let i = 0; i < 6; i++) {
          const sh = 0.05 + rng() * 0.16;
          const m = M(new THREE.BoxGeometry(0.12 + rng() * 0.08, sh, 0.3), body, g);
          m.position.set(-0.42 + i * 0.17, 0.18 + sh / 2, 0);
          m.rotation.z = (rng() - 0.5) * 0.2;
        }
        fallen(g, 9, 1.0, 0.24, rng);
        fallen(g, 9, 1.0, -0.24, rng);
      }
      h = 0.4;
      break;
    }
    case 'gate': {
      const W = 1.2;
      const D = 0.5;
      const tw = 0.36;
      const tx = W / 2 - tw / 2;
      if (mat === 2) {
        for (const x of [-tx, tx]) stump(tw * 0.72, tw * 0.75, 0.3 + rng() * 0.1, 4, '#8f887c', x, 0);
        fallen(g, 10, W, D / 2 + 0.05, rng);
        fallen(g, 10, W, -D / 2 - 0.05, rng);
      } else {
        for (const x of [-tx, tx]) {
          stakes([[x - 0.12, D / 2], [x, D / 2], [x + 0.12, D / 2], [x - 0.12, -D / 2], [x + 0.06, -D / 2], [x + 0.12, -D / 2]], 0.045);
          scorch(0.26, x, 0);
        }
        const roof = M(new THREE.ConeGeometry((tw + 0.04) * Math.SQRT1_2, 0.18, 4), std('#3a271a', { roughness: 0.95, flatShading: true }), g);
        roof.position.set(tx + 0.25, 0.1, -D / 2 - 0.2);
        roof.rotation.set(0.6, Math.PI / 4, 1.2);
        splinters(g, 10, 0.4, rng);
      }
      // The portcullis, torn down and lying across the gateway.
      const port = G(g, 0, 0.03, D / 2 + 0.12);
      const barM = mat === 2 ? DSTEEL : DWOOD;
      const br = mat === 2 ? 0.013 : 0.022;
      for (let i = 0; i < 5; i++) limb(port, V(-0.2 + i * 0.1, 0, 0), V(-0.2 + i * 0.1, 0, 0.5), br, br, barM, 5);
      for (let j = 0; j < 3; j++) limb(port, V(-0.23, br, 0.08 + j * 0.17), V(0.23, br, 0.08 + j * 0.17), br * 0.85, br * 0.85, barM, 5);
      port.rotation.y = 0.15;
      h = 0.5;
      break;
    }
    case 'forge': {
      const st = stone('#6d675e');
      scorch(0.36, -0.1, -0.15);
      // Low broken walls around the hearth.
      for (const [x, z, w, d] of [[-0.1, 0.13, 0.8, 0.06], [-0.1, -0.43, 0.8, 0.06], [-0.48, -0.15, 0.06, 0.6], [0.28, -0.15, 0.06, 0.6]] as const) {
        const sh = 0.1 + rng() * 0.18;
        M(new THREE.BoxGeometry(w, sh, d), st, g).position.set(x, sh / 2, z);
      }
      const chim = M(new THREE.CylinderGeometry(0.09, 0.11, 0.5, 8), st, g);
      chim.position.set(0.55, 0.1, -0.5);
      chim.rotation.set(0, 0.5, Math.PI / 2);
      // The anvil stands where it stood.
      M(new THREE.BoxGeometry(0.28, 0.1, 0.12), DSTEEL, g).position.set(0.45, 0.32, 0.3);
      M(new THREE.ConeGeometry(0.05, 0.12, 4), DSTEEL, g).position.set(0.62, 0.32, 0.3);
      limb(g, V(0.45, 0, 0.3), V(0.45, 0.27, 0.3), 0.06, 0.05, DWOOD, 7);
      rubbleAround(g, 12, 0.5, rng, ['#6d675e', '#625c54', '#3e3a36'], -0.1, -0.15);
      break;
    }
    case 'barracks': {
      M(new THREE.BoxGeometry(1.0, 0.1, 0.66), stone('#6d675e'), g).position.y = 0.05;
      scorch(0.5, 0, 0, 1.5);
      for (const x of [-0.45, -0.15, 0.15, 0.45]) for (const z of [-0.28, 0.28]) {
        const sh = 0.06 + rng() * 0.3;
        M(new THREE.BoxGeometry(0.05, sh, 0.05), charred, g).position.set(x, 0.1 + sh / 2, z);
      }
      // The roof came down in charred planks.
      for (let i = 0; i < 6; i++) {
        const p = M(new THREE.BoxGeometry(0.5 + rng() * 0.3, 0.025, 0.09), i % 2 ? charred : std('#4a2f22', { roughness: 0.95, flatShading: true }), g);
        p.position.set((rng() - 0.5) * 0.6, 0.12 + i * 0.012, (rng() - 0.5) * 0.4);
        p.rotation.set((rng() - 0.5) * 0.2, (rng() - 0.5) * 0.8, (rng() - 0.5) * 0.15);
      }
      splinters(g, 8, 0.55, rng);
      h = 0.4;
      break;
    }
    case 'temple': {
      const st = stone('#a9a397');
      M(new THREE.BoxGeometry(1.0, 0.08, 1.0), stone('#57534d'), g).position.y = 0.04;
      M(new THREE.BoxGeometry(0.86, 0.08, 0.86), st, g).position.y = 0.12;
      let k = 0;
      for (const x of [-0.34, 0.34]) for (const z of [-0.34, 0.34]) {
        const sh = [0.5, 0.14, 0.22, 0.08][k++] as number;
        M(new THREE.CylinderGeometry(0.06, 0.065, sh, 8), st, g).position.set(x, 0.16 + sh / 2, z);
        snapTop(g, x, 0.16 + sh, z, 0.05, st, rng);
      }
      const drum = M(new THREE.CylinderGeometry(0.055, 0.06, 0.45, 8), st, g);
      drum.position.set(0.15, 0.22, 0.5);
      drum.rotation.set(0, 0.4, Math.PI / 2);
      M(new THREE.CylinderGeometry(0.06, 0.09, 0.3, 8), st, g).position.y = 0.31;
      M(new THREE.CylinderGeometry(0.17, 0.11, 0.1, 10), st, g).position.y = 0.5;
      const water = M(new THREE.CircleGeometry(0.15, 12), emis('#a8c4d0', '#7fd6ff', 0.25), g);
      water.rotation.x = -Math.PI / 2;
      water.position.y = 0.552;
      rubbleAround(g, 12, 0.55, rng, ['#a9a397', '#9d978b', '#3e3a36']);
      h = 0.7;
      break;
    }
    default: {
      rubbleAround(g, 14, 0.3, rng);
    }
  }
  return done(c, h);
};

/* ---------- traps ---------- */

export interface SpikesOpts extends BaseOpts {
  /** Remaining charges ratio 0..1: fewer stakes as it wears out. */
  charges?: number;
  /** Gallery only: a shadow caught in the pit. */
  victim?: boolean;
}

export const spikes = (o?: SpikesOpts): Model => {
  const c = begin(o, 0.7, 70);
  const { g, R, rng } = c;
  const pit = M(new THREE.CircleGeometry(0.45, 18), BLACK, g);
  pit.rotation.x = -Math.PI / 2;
  pit.position.y = 0.01;
  ring(g, 0.46, 0.04, stone('#5a5148'), 0, 5, 22);
  const n = Math.round(14 * Math.max(0, Math.min(1, o?.charges ?? 1)));
  for (let i = 0; i < 14; i++) {
    const a = rng() * Math.PI * 2, r = rng() * 0.38;
    const rx = R() * 0.4, rz = R() * 0.4;
    if (i >= n) continue;
    const s = M(new THREE.ConeGeometry(0.025, 0.32, 5), WOOD, g);
    s.position.set(Math.cos(a) * r, 0.12, Math.sin(a) * r);
    s.rotation.set(rx, 0, rz);
  }
  let t: ((n: number) => void) | undefined;
  if (o?.victim) {
    const vic = shadowVictim(g);
    t = (k) => { vic.position.y = 0.22 + (k % 10 < 5 ? 0.03 : 0); };
  }
  return done(c, 0.3, t);
};

/** NEW: boiling-oil cauldron on an iron tripod over a dark slick. */
export const oil = (o?: BaseOpts): Model => {
  const c = begin(o, 0.62, 71);
  const { g, R, rng } = c;
  const slickGeo = new THREE.CircleGeometry(0.46, 16);
  const sp = slickGeo.attributes.position as THREE.BufferAttribute;
  for (let i = 1; i < sp.count; i++) {
    const k = 0.8 + rng() * 0.3;
    sp.setXY(i, sp.getX(i) * k, sp.getY(i) * k);
  }
  const slick = M(slickGeo, std('#0a0806', { roughness: 0.05, metalness: 0.4, bumpScale: 0 }), g);
  slick.rotation.x = -Math.PI / 2;
  slick.position.y = 0.008;
  for (let i = 0; i < 3; i++) {
    const a = (i / 3) * Math.PI * 2 + 0.3;
    limb(g, V(Math.cos(a) * 0.36, 0, Math.sin(a) * 0.36), V(0, 0.9, 0), 0.018, 0.022, DSTEEL, 5);
  }
  const pot = G(g, 0, 0.45, 0);
  limb(g, V(0, 0.9, 0), V(0, 0.68, 0), 0.006, 0.006, DSTEEL, 4);
  const bowl = M(new THREE.SphereGeometry(0.2, 12, 6, 0, Math.PI * 2, Math.PI / 2, Math.PI / 2), metal('#2e2c30', { side: THREE.DoubleSide, roughness: 0.5 }), pot);
  bowl.position.y = 0.12;
  ring(pot, 0.2, 0.018, DSTEEL, 0.12, 4, 18);
  const surf = M(new THREE.CircleGeometry(0.19, 14), std('#14100a', { roughness: 0.05, metalness: 0.5, bumpScale: 0 }), pot);
  surf.rotation.x = -Math.PI / 2;
  surf.position.y = 0.1;
  for (const k of [-1, 1]) {
    const h = M(new THREE.TorusGeometry(0.05, 0.008, 4, 10, Math.PI), DSTEEL, pot);
    h.position.set(k * 0.2, 0.15, 0);
    h.rotation.y = Math.PI / 2;
  }
  const bubbles: THREE.Mesh[] = [];
  const bm = std('#2a2016', { roughness: 0.1, metalness: 0.3, bumpScale: 0 });
  for (let i = 0; i < 4; i++) {
    const b = M(new THREE.SphereGeometry(0.025, 6, 4), bm, pot);
    b.position.set(R() * 0.25, 0.1, R() * 0.25);
    bubbles.push(b);
  }
  // Fire under the pot.
  const coals = emis('#ff6a1a', '#ff3a00', 1.2);
  for (let i = 0; i < 5; i++) M(new THREE.DodecahedronGeometry(0.04), coals, g).position.set(R() * 0.18, 0.03, R() * 0.18);
  const fl = flame(g, V(0, 0.03, 0), 0.9, rng);
  const L = pointLight(c, 0xff8a2a, 0.8, 2.5, V(0, 0.25, 0));
  const t = (n: number): void => {
    fl.flick();
    bubbles.forEach((b, i) => { b.scale.setScalar((n + i) % 4 === 0 ? 0.2 : 1 + ((n + i) % 3) * 0.3); });
    L.intensity = (0.7 + rng() * 0.3) * LIGHT_K;
  };
  t(0);
  return done(c, 0.95, t);
};

/* ---------- support buildings ---------- */

export const gable = (w: number, h: number, len: number, mat: THREE.Material, parent: THREE.Object3D): THREE.Mesh => {
  const s = new THREE.Shape();
  s.moveTo(-w / 2, 0);
  s.lineTo(w / 2, 0);
  s.lineTo(0, h);
  s.closePath();
  const geo = new THREE.ExtrudeGeometry(s, { depth: len, bevelEnabled: false });
  geo.translate(0, 0, -len / 2);
  geo.rotateY(Math.PI / 2);
  return M(geo, mat, parent);
};

/** NEW: timber barracks hall with a weapon rack and a banner. */
export const barracks = (o?: BaseOpts & { wear?: Wear }): Model => {
  const wear = o?.wear ?? 0;
  const c = begin(o, 0.75, 73);
  const { g } = c;
  const wr = mulberry32(730 + wear);
  M(new THREE.BoxGeometry(1.0, 0.1, 0.66), stone('#6d675e'), g).position.y = 0.05;
  M(new THREE.BoxGeometry(0.92, 0.42, 0.56), std('#8a6a4a', { roughness: 1 }), g).position.y = 0.31;
  for (const x of [-0.45, -0.15, 0.15, 0.45]) for (const z of [-0.28, 0.28]) M(new THREE.BoxGeometry(0.05, 0.44, 0.05), DWOOD, g).position.set(x, 0.32, z);
  for (const z of [-0.285, 0.285]) {
    M(new THREE.BoxGeometry(0.94, 0.05, 0.03), DWOOD, g).position.set(0, 0.5, z);
    for (const x of [-0.3, 0, 0.3]) {
      const br = M(new THREE.BoxGeometry(0.03, 0.4, 0.02), DWOOD, g);
      br.position.set(x, 0.3, z);
      br.rotation.z = x === 0 ? 0 : 0.6 * Math.sign(x);
    }
  }
  const roofM = std('#4a2f22', { roughness: 0.95, flatShading: true });
  const roof = gable(0.74, 0.36, 1.06, roofM, g);
  roof.position.y = 0.52;
  M(new THREE.BoxGeometry(1.08, 0.04, 0.05), DWOOD, g).position.set(0, 0.88, 0);
  // Door and shutters on the front (+Z).
  M(new THREE.BoxGeometry(0.16, 0.3, 0.02), DWOOD, g).position.set(0, 0.25, 0.29);
  for (const x of [-0.3, 0.3]) M(new THREE.BoxGeometry(0.1, 0.08, 0.02), BLACK, g).position.set(x, 0.36, 0.29);
  if (wear > 0) {
    // Scorched planks on both long walls; a shutter hanging by one hinge.
    for (let i = 0; i < wear * 2; i++) for (const side of [1, -1]) stain(g, (wr() - 0.5) * 0.8, 0.2 + wr() * 0.22, side * 0.306, side > 0 ? 0 : Math.PI, 0.07 + wr() * 0.05, wr, '#2e241b');
    const sh = M(new THREE.BoxGeometry(0.1, 0.08, 0.015), DWOOD, g);
    sh.position.set(0.37, 0.33, 0.305);
    sh.rotation.set(0, 0.5, 0.7);
    // A scorched patch on the front wall, and two wall boards torn off (one hangs, one lies at the foot).
    stain(g, -0.16, 0.3, 0.307, 0, 0.13, wr, '#241c15');
    M(new THREE.BoxGeometry(0.06, 0.3, 0.012), std('#1c1611', { roughness: 1 }), g).position.set(0.15, 0.3, 0.29);
    const board = M(new THREE.BoxGeometry(0.06, 0.3, 0.015), std('#8a6a4a', { roughness: 1 }), g);
    board.position.set(0.19, 0.24, 0.32);
    board.rotation.set(0.15, 0, 0.45);
    const lying = M(new THREE.BoxGeometry(0.06, 0.015, 0.32), std('#8a6a4a', { roughness: 1 }), g);
    lying.position.set(-0.3, 0.012, 0.45);
    lying.rotation.y = 1.1;
    splinters(g, wear === 2 ? 8 : 6, 0.55, wr);
  }
  if (wear > 0) {
    // Boards gone from both slopes: dark gaps between the shingles.
    const slope = Math.atan2(0.36, 0.37);
    for (let i = 0; i < wear * 2 + 1; i++) {
      const side = i % 2 ? -1 : 1;
      const k = 0.25 + wr() * 0.5;
      const gap = M(new THREE.BoxGeometry(0.12 + wr() * 0.1, 0.01, 0.05), std('#140f0c', { roughness: 1 }), g);
      gap.rotation.x = side * slope;
      gap.position.set((wr() - 0.5) * 0.8, 0.52 + 0.36 * (1 - k) + 0.008, side * 0.37 * k);
    }
    // Shingles knocked loose, lying askew on the front slope (lighter, so they catch the eye).
    for (let i = 0; i < 2; i++) {
      const k = 0.35 + i * 0.3;
      const sh = M(new THREE.BoxGeometry(0.16, 0.014, 0.08), std('#6b4a32', { roughness: 0.95, flatShading: true }), g);
      sh.rotation.set(slope, 0.5 - i, 0);
      sh.position.set(-0.25 + i * 0.35, 0.52 + 0.36 * (1 - k) + 0.02, 0.37 * k);
    }
  }
  if (wear === 2) {
    // Burnt through on the front slope: a charred hole with broken rafters.
    const slope = Math.atan2(0.36, 0.37);
    const hole = M(new THREE.BoxGeometry(0.34, 0.012, 0.24), std('#120e0b', { roughness: 1 }), g);
    hole.rotation.x = slope;
    hole.position.set(0.18, 0.52 + 0.18 + 0.01, 0.185 + 0.01);
    for (const x of [0.08, 0.26]) {
      const raf = M(new THREE.BoxGeometry(0.03, 0.03, 0.3), std('#2b2119', { roughness: 1 }), g);
      raf.rotation.x = slope;
      raf.position.set(x, 0.52 + 0.19, 0.19);
    }
  }
  // Weapon rack.
  const rack = G(g, 0.32, 0.1, 0.44);
  for (const x of [-0.12, 0.12]) limb(rack, V(x, 0, 0), V(x, 0.42, 0), 0.015, 0.015, DWOOD, 5);
  M(new THREE.BoxGeometry(0.3, 0.025, 0.03), DWOOD, rack).position.y = 0.36;
  for (let i = 0; i < 3; i++) {
    const x = -0.08 + i * 0.08;
    limb(rack, V(x, 0, 0.03), V(x, 0.6, 0.0), 0.007, 0.007, WOOD, 4);
    M(new THREE.ConeGeometry(0.015, 0.07, 4), STEEL, rack).position.set(x, 0.63, 0.0);
  }
  const sh = M(new THREE.CylinderGeometry(0.09, 0.09, 0.02, 10), WOOD, rack);
  sh.rotation.x = Math.PI / 2 - 0.2;
  sh.position.set(-0.2, 0.12, 0.04);
  ring(rack, 0.09, 0.008, DSTEEL, 0, 3, 12).rotation.x = -0.2;
  // Banner (crumbling: its pole leans and the cloth is torn short).
  const flag = G(g, -0.5, 0.1, 0.33);
  if (wear === 2) flag.rotation.set(0.12, 0, 0.3);
  limb(flag, V(0, 0, 0), V(0, 1.1, 0), 0.014, 0.014, DWOOD, 5);
  M(new THREE.BoxGeometry(0.22, wear === 2 ? 0.2 : 0.32, 0.01), cloth(ROYAL), flag).position.set(0.12, wear === 2 ? 0.96 : 0.9, 0);
  if (wear < 2) M(new THREE.BoxGeometry(0.22, 0.04, 0.012), cloth('#6e1f24'), flag).position.set(0.12, 0.75, 0);
  return done(c, 1.2);
};

/** NEW: small stone shrine with columns and a glowing pale-blue font. */
/**
 * Worn (1): cracked columns, stained lintel, stones at the foot and a dimmer font. Crumbling (2): one
 * column broken (its drum on the ground) and the roof sagging onto that corner; the font barely glows.
 */
export const temple = (o?: BaseOpts & { wear?: Wear }): Model => {
  const wear = o?.wear ?? 0;
  const c = begin(o, 0.75, 74);
  const { g } = c;
  const wr = mulberry32(740 + wear);
  const st = stone('#a9a397'), dk = stone('#57534d');
  M(new THREE.BoxGeometry(1.0, 0.08, 1.0), dk, g).position.y = 0.04;
  M(new THREE.BoxGeometry(0.86, 0.08, 0.86), st, g).position.y = 0.12;
  for (const x of [-0.34, 0.34]) for (const z of [-0.34, 0.34]) {
    if (wear === 2 && x > 0 && z > 0) {
      // Its top drum broke away; the sagging roof now rests on the stump.
      M(new THREE.CylinderGeometry(0.06, 0.065, 0.53, 8), st, g).position.set(x, 0.16 + 0.265, z);
      const drum = M(new THREE.CylinderGeometry(0.055, 0.06, 0.2, 8), st, g);
      drum.position.set(x + 0.28, 0.06, z + 0.12);
      drum.rotation.set(0, 0.6, Math.PI / 2);
      continue;
    }
    M(new THREE.CylinderGeometry(0.055, 0.065, 0.7, 8), st, g).position.set(x, 0.51, z);
    if (wear === 1 && x > 0 && z > 0) {
      // A cracked capital: split in two, half of it fallen at the column's foot.
      M(new THREE.BoxGeometry(0.07, 0.05, 0.14), st, g).position.set(x - 0.035, 0.885, z);
      const half = M(new THREE.BoxGeometry(0.07, 0.05, 0.14), st, g);
      half.position.set(x + 0.17, 0.185, z + 0.12);
      half.rotation.set(0.3, 0.7, 0.2);
    } else M(new THREE.BoxGeometry(0.14, 0.05, 0.14), st, g).position.set(x, 0.885, z);
    if (wear > 0) {
      // Facing out of the shrine, where the table sees it.
      const a = Math.atan2(x, z);
      crack(g, x + Math.sin(a) * 0.066, 0.75 - wr() * 0.2, z + Math.cos(a) * 0.066, a, wear === 2 ? 0.32 : 0.2, wr);
    }
  }
  // The upper works hinge on the far corner and sag onto the broken column (+X +Z) when crumbling.
  const top = G(g);
  const off = V(0, 0, 0);
  if (wear === 2) {
    // Drops ≈ 0.22 at the broken corner, onto the stump's top (0.69).
    top.position.set(-0.34, 0.91, -0.34);
    top.rotation.set(0.16, 0, -0.16);
    off.set(0.34, -0.91, 0.34);
  }
  M(new THREE.BoxGeometry(0.86, 0.08, 0.86), st, top).position.set(off.x, off.y + 0.95, off.z);
  const roof = M(new THREE.ConeGeometry(0.64, 0.36, 4), stone('#3e3a36'), top);
  roof.position.set(off.x, off.y + 1.17, off.z);
  roof.rotation.y = Math.PI / 4;
  if (wear > 0) {
    for (const [x, z, ry] of [[0, 0.432, 0], [0, -0.432, Math.PI], [0.432, 0, Math.PI / 2], [-0.432, 0, -Math.PI / 2]] as const) stain(top, off.x + x, off.y + 0.95, off.z + z, ry, 0.06 + wr() * 0.03, wr);
    if (wear === 1) {
      // A corner of the lintel broken off over the cracked capital.
      M(new THREE.BoxGeometry(0.16, 0.085, 0.16), stone('#4a4640'), top).position.set(0.36, 0.95, 0.36);
    }
    rubbleAround(g, wear === 2 ? 10 : 7, 0.52, wr, ['#a9a397', '#9d978b', '#57534d']);
  }
  // Font.
  M(new THREE.CylinderGeometry(0.06, 0.09, 0.3, 8), st, g).position.y = 0.31;
  const basin = M(new THREE.CylinderGeometry(0.17, 0.11, 0.1, 10), st, g);
  basin.position.y = 0.5;
  const water = M(new THREE.CircleGeometry(0.15, 12), emis('#dff4ff', '#7fd6ff', [1.1, 0.7, 0.35][wear] as number), g);
  water.rotation.x = -Math.PI / 2;
  water.position.y = 0.552;
  glowSprite(g, '#9fe0ff', [0.7, 0.45, 0.2][wear] as number, V(0, 0.6, 0), 0.6);
  const motes: THREE.Sprite[] = [];
  for (let i = 0; i < 3; i++) motes.push(glowSprite(g, '#cfefff', 0.08, V(0, 0.6, 0), 0.9, true));
  const L = pointLight(c, 0x9fdcff, 0.8, 2.5, V(0, 0.7, 0));
  const t = (n: number): void => {
    motes.forEach((m, i) => {
      const p = (n * 0.05 + i / 3) % 1, a = i * 2.1 + p * 4;
      m.position.set(Math.cos(a) * 0.1, 0.58 + p * 0.3, Math.sin(a) * 0.1);
      m.material.opacity = 0.9 * (1 - p);
    });
    L.intensity = (0.8 + (n % 6 < 3 ? 0.1 : 0)) * LIGHT_K;
  };
  t(0);
  return done(c, 1.35, t);
};

/* ---------- Somnhold (era V): the titan's stone and the Dream Beacon ---------- */

const SILVER = metal('#c9d0da', { roughness: 0.3 });
/** The titan's stone: dark, cold, alive (era V walls, the keep's step, the forge of Somnhold). */
export const TITAN_STONE = '#3e434b';
/** Shell plates set in it, a little paler than the stone. */
export const TITAN_PLATES = ['#59606a', '#525962', '#606872'] as const;
/** Its runes: a faint blue-white, the Dream Beacon's light (never gold). */
export const TITAN_RUNE = emis('#e4f1ff', '#8fc0ff', 1.1);
/** The beacon's lens. */
const DREAM_LENS = emis('#f4f9ff', '#a8d0ff', 2.2);
/** The great bell: old bronze gone green, so it reads apart from the stone and is never gold. */
const BELL = metal('#557a72', { roughness: 0.45, metalness: 0.6 });

/** A hexagonal shell plate of radius r on a wall face: centre (x, y, z), its flat side facing `ry` (0: +Z). */
export const shellPlate = (parent: THREE.Object3D, x: number, y: number, z: number, r: number, ry: number, mat: THREE.Material, thick = 0.022): THREE.Mesh => {
  // Open sides and the outer face only (the back is against the wall): 18 triangles, not 24.
  const p = M(new THREE.CylinderGeometry(r * 0.86, r, thick, 6, 1, true), mat, parent);
  const cap = M(new THREE.CircleGeometry(r * 0.86, 6), mat, p);
  cap.rotation.set(-Math.PI / 2, 0, Math.PI / 6);
  cap.position.y = thick / 2;
  p.rotation.order = 'YXZ';
  p.rotation.set(Math.PI / 2, ry, 0);
  p.position.set(x, y, z);
  return p;
};

/** The keep's era V step: a broad slab of the titan's stone round the plinth, shell plates and pale runes on its rim. */
const titanStep = (g: THREE.Group): void => {
  M(new THREE.BoxGeometry(1.44, 0.045, 1.44), stone(TITAN_STONE), g).position.y = 0.0225;
  for (const k of [-1, 1]) {
    for (const i of [-2, -1, 0, 1, 2]) {
      const mat = stone(TITAN_PLATES[(i + 2 + (k > 0 ? 1 : 0)) % 3] as string);
      for (const [x, z] of [[i * 0.26, k * 0.68], [k * 0.68, i * 0.26]] as const) {
        M(new THREE.CylinderGeometry(0.075, 0.085, 0.014, 6), mat, g).position.set(x, 0.05, z);
      }
    }
    M(new THREE.BoxGeometry(1.2, 0.012, 0.012), TITAN_RUNE, g).position.set(0, 0.032, k * 0.723);
    M(new THREE.BoxGeometry(0.012, 0.012, 1.2), TITAN_RUNE, g).position.set(k * 0.723, 0.032, 0);
  }
};

/**
 * Era V's crown on the keep, from `base` up: an open belfry of pale stone with the great bell in its arches, a drum of
 * the titan's stone ringed with runes, and the Dream Beacon: a blue-white lens in a cage of silver ribs under a slender
 * spire. Returns the lens's height.
 */
const belfryAndBeacon = (parent: THREE.Group, b0: number, pale: THREE.Material, st: THREE.Material, roofM: THREE.Material, rng: () => number): number => {
  // Built at unit size, then grown so the crown carries from the far table camera.
  const S = 1.3;
  const g = G(parent, 0, b0, 0);
  g.scale.setScalar(S);
  const base = 0;
  const titan = stone(TITAN_STONE);
  // The belfry: four piers, an arch on every face, a cornice, a pinnacle on each corner.
  const H = 0.46, w = 0.17;
  M(new THREE.BoxGeometry(0.44, 0.04, 0.44), st, g).position.y = base + 0.02;
  for (const x of [-w, w]) {
    for (const z of [-w, w]) {
      M(new THREE.BoxGeometry(0.07, H, 0.07), pale, g).position.set(x, base + 0.04 + H / 2, z);
      M(new THREE.ConeGeometry(0.035, 0.16, 4), roofM, g).position.set(x, base + 0.04 + H + 0.13, z);
      M(new THREE.SphereGeometry(0.016, 6, 4), SILVER, g).position.set(x, base + 0.04 + H + 0.22, z);
    }
  }
  for (const k of [-1, 1]) {
    const a1 = M(new THREE.TorusGeometry(0.135, 0.026, 4, 12, Math.PI), pale, g);
    a1.position.set(0, base + 0.04 + H - 0.14, k * w);
    const a2 = M(new THREE.TorusGeometry(0.135, 0.026, 4, 12, Math.PI), pale, g);
    a2.position.set(k * w, base + 0.04 + H - 0.14, 0);
    a2.rotation.y = Math.PI / 2;
    // A silver rail across each arch's sill.
    M(new THREE.BoxGeometry(0.28, 0.018, 0.02), SILVER, g).position.set(0, base + 0.12, k * w);
    M(new THREE.BoxGeometry(0.02, 0.018, 0.28), SILVER, g).position.set(k * w, base + 0.12, 0);
  }
  const top = base + 0.04 + H;
  M(new THREE.BoxGeometry(0.46, 0.05, 0.46), st, g).position.y = top + 0.025;
  M(new THREE.BoxGeometry(0.47, 0.016, 0.47), SILVER, g).position.y = top + 0.004;
  // The great bell on its yoke: a waist flaring to the lip, its mouth dark, the clapper hanging in it.
  const bellTop = top - 0.07;
  M(new THREE.BoxGeometry(0.36, 0.035, 0.04), DWOOD, g).position.set(0, bellTop + 0.03, 0);
  const prof: THREE.Vector2[] = [new THREE.Vector2(0, 0)];
  for (let i = 0; i <= 8; i++) {
    const t = i / 8;
    prof.push(new THREE.Vector2(0.05 + 0.055 * t * t + (i === 8 ? 0.018 : 0), -t * 0.24));
  }
  M(new THREE.LatheGeometry(prof, 12), BELL, g).position.y = bellTop;
  const mouth = M(new THREE.CircleGeometry(0.115, 12), BLACK, g);
  mouth.rotation.x = Math.PI / 2;
  mouth.position.y = bellTop - 0.238;
  M(new THREE.SphereGeometry(0.025, 6, 4), DSTEEL, g).position.y = bellTop - 0.21;
  const lip = M(new THREE.TorusGeometry(0.105, 0.01, 4, 14), BELL, g);
  lip.position.y = bellTop - 0.2;
  lip.rotation.x = Math.PI / 2;
  // The beacon's drum: the titan's stone, a band of pale runes round it, a silver lip.
  const d0 = top + 0.05;
  const drum = M(new THREE.CylinderGeometry(0.15, 0.18, 0.16, 8), titan, g);
  drum.position.y = d0 + 0.08;
  drum.rotation.y = Math.PI / 8;
  const band = M(new THREE.CylinderGeometry(0.163, 0.17, 0.022, 8, 1, true), TITAN_RUNE, g);
  band.position.y = d0 + 0.08;
  band.rotation.y = Math.PI / 8;
  M(new THREE.CylinderGeometry(0.17, 0.15, 0.03, 8), SILVER, g).position.y = d0 + 0.175;
  // The lens in its cage of silver ribs, a ring round its waist, the spire above.
  const faro = d0 + 0.34;
  const lens = M(new THREE.IcosahedronGeometry(0.105, 0), DREAM_LENS, g);
  lens.position.y = faro;
  lens.rotation.y = rng() * 0.5;
  const girdle = M(new THREE.TorusGeometry(0.125, 0.012, 4, 16), SILVER, g);
  girdle.position.y = faro;
  girdle.rotation.x = Math.PI / 2;
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2;
    const p0 = V(Math.cos(a) * 0.14, d0 + 0.18, Math.sin(a) * 0.14);
    const p1 = V(Math.cos(a) * 0.15, faro, Math.sin(a) * 0.15);
    const p2 = V(Math.cos(a) * 0.03, faro + 0.22, Math.sin(a) * 0.03);
    limb(g, p0, p1, 0.011, 0.011, SILVER, 4);
    limb(g, p1, p2, 0.011, 0.008, SILVER, 4);
  }
  M(new THREE.ConeGeometry(0.05, 0.3, 6), SILVER, g).position.y = faro + 0.36;
  M(new THREE.SphereGeometry(0.024, 8, 6), SILVER, g).position.y = faro + 0.53;
  glowSprite(g, '#cfe4ff', 1.9, V(0, faro, 0), 0.8);
  glowSprite(g, '#ffffff', 0.6, V(0, faro, 0), 0.9);
  glowSprite(g, '#8fc0ff', 0.5, V(0, d0 + 0.08, 0), 0.4);
  return b0 + faro * S;
};

/**
 * Where a bracket can stand on the keep's crown when it ends in a point (the night of the bell's trophy hangs off it,
 * models/trophies.ts `lidBellTrophy`): its height and how far out from the axis, in the keep's frame, from the keep's
 * `height`. Era V: the belfry roof (in `belfryAndBeacon`'s units ×1.3 the lens is at 0.89 and the roof at 0.55; the
 * keep's height is 0.72 over the lens), outside the beacon's drum. Era IV: the lantern's cornice (0.48 over its base,
 * the keep 0.86), inside its edge. Below that the crown is flat enough to stand over.
 */
export const keepCrownBracket = (era: number, height: number): { y: number; x: number } | null =>
  era >= 5 ? { y: height - (0.72 + (0.89 - 0.55) * 1.3), x: 0.26 } : era === 4 ? { y: height - (0.86 - 0.48), x: 0.15 } : null;

/**
 * The central keep tower (~1.1 footprint), royal-blue banners and a lit brazier. It grows with the bastion's era
 * (docs/PROGRESION.md, the user's ask: every era has to show):
 * - I (palisade): the square keep, four turrets with conical roofs, the brazier on the walk (~2.5 tall);
 * - II (citadel): a second, narrower stage rises from the walk with its own merlons and bartizans, iron bands
 *   girdle the keep, long banners hang down the faces and red pennants fly from the turrets (~3.1);
 * - III (Titan's fortress): the upper stage grows a third tier, flying buttresses leap from the turrets to it,
 *   iron spikes crown the merlons, runes burn dragon red round the plinth and a great beacon cage burns on top (~3.7);
 * - IV (the Vigil's bastion): the Order's seat again. A fourth tier, an arched lantern where the Vigil's flame burns
 *   silver-white (never gold), the Order's long crimson standards down every face and silver bands on the stone (~4.4).
 * - V (Somnhold): the whole fortress as it was before the Night of the Gate, as the cinematics show it. The fourth tier
 *   grows taller, a belfry opens on top with the great bell hanging in its arches, and over it the Dream Beacon: a
 *   blue-white lens in a silver cage (the view makes it beat, see `somnsky.ts`). The plinth gains a step of the
 *   titan's stone with its shell plates and pale runes, and the turrets grow tall spires with silver finials (~5.1).
 */
export const keep = (o?: BaseOpts & { era?: number }): Model => {
  const era = Math.min(5, Math.max(1, o?.era ?? 1));
  const c = begin(o, 0.8, 75);
  const { g, rng } = c;
  // Somnhold's keep (era V) is dressed in a cold pale stone, its roofs a blue slate: it reads apart at a glance.
  const somn = era >= 5;
  const st = stone(somn ? '#8e949c' : '#857f74'), dk = stone(somn ? '#50555d' : '#5f5b55'), roofM = stone(somn ? '#2e333b' : '#3e3a36'), mortar = std('#3a3632');
  const rune = emis('#ffc0a8', '#ff4f3a', 1.4);
  const crimson = cloth('#7a1d22');
  // The plinth: one step in era I, two from era II (the second dark, runed in era III).
  M(new THREE.BoxGeometry(1.1, 0.14, 1.1), dk, g).position.y = 0.07;
  if (era >= 2) {
    const p2 = M(new THREE.BoxGeometry(1.26, 0.06, 1.26), stone('#4d4944'), g);
    p2.position.y = 0.03;
  }
  if (era >= 3) {
    for (const k of [-1, 1]) for (const i of [-1, 0, 1]) {
      M(new THREE.BoxGeometry(0.12, 0.03, 0.01), rune, g).position.set(i * 0.3, 0.035, k * 0.633);
      M(new THREE.BoxGeometry(0.01, 0.03, 0.12), rune, g).position.set(k * 0.633, 0.035, i * 0.3);
    }
    for (const k of [-1, 1]) glowSprite(g, '#ff4f3a', 0.5, V(k * 0.4, 0.06, 0.66), 0.35);
  }
  if (era >= 5) titanStep(g);
  M(new THREE.BoxGeometry(0.8, 1.72, 0.8), st, g).position.y = 0.14 + 0.86;
  for (const y of [0.5, 0.9, 1.3]) for (const k of [-1, 1]) {
    M(new THREE.BoxGeometry(0.81, 0.012, 0.004), mortar, g).position.set(0, y, k * 0.401);
    M(new THREE.BoxGeometry(0.004, 0.012, 0.81), mortar, g).position.set(k * 0.401, y, 0);
  }
  // Iron bands girdle the keep from era II.
  if (era >= 2) for (const y of [0.62, 1.5]) M(new THREE.BoxGeometry(0.83, 0.035, 0.83), IRON_BAND, g).position.y = y;
  // Corner turrets: conical roofs in era I, crenellated caps with red pennants after.
  for (const x of [-0.42, 0.42]) for (const z of [-0.42, 0.42]) {
    M(new THREE.CylinderGeometry(0.13, 0.15, 1.95, 8), st, g).position.set(x, 0.14 + 0.975, z);
    if (era === 1) {
      M(new THREE.ConeGeometry(0.16, 0.3, 8), roofM, g).position.set(x, 2.24, z);
      continue;
    }
    M(new THREE.CylinderGeometry(0.17, 0.14, 0.12, 8), st, g).position.set(x, 2.15, z);
    for (let i = 0; i < 4; i++) {
      const a = (i * Math.PI) / 2 + Math.PI / 4;
      M(new THREE.BoxGeometry(0.06, 0.08, 0.06), st, g).position.set(x + Math.sin(a) * 0.13, 2.25, z + Math.cos(a) * 0.13);
    }
    // Somnhold's turrets rise a stage higher: a slender shaft girt in silver, a crown and a tall slate spire.
    const lift = era >= 5 ? 0.86 : 0;
    if (era >= 5) {
      M(new THREE.CylinderGeometry(0.085, 0.1, 0.84, 8), st, g).position.set(x, 2.25 + 0.42, z);
      M(new THREE.CylinderGeometry(0.104, 0.104, 0.025, 8), SILVER, g).position.set(x, 2.62, z);
      M(new THREE.BoxGeometry(0.02, 0.1, 0.02), TITAN_RUNE, g).position.set(x + Math.sign(x) * 0.09, 2.78, z);
      M(new THREE.BoxGeometry(0.02, 0.1, 0.02), TITAN_RUNE, g).position.set(x, 2.78, z + Math.sign(z) * 0.09);
      M(new THREE.CylinderGeometry(0.125, 0.09, 0.09, 8), st, g).position.set(x, 3.12, z);
      M(new THREE.ConeGeometry(0.12, 0.5, 8), roofM, g).position.set(x, 3.42, z);
      M(new THREE.SphereGeometry(0.022, 6, 4), SILVER, g).position.set(x, 3.68, z);
    } else M(new THREE.ConeGeometry(0.11, 0.22, 8), roofM, g).position.set(x, 2.32, z);
    limb(g, V(x, 2.4 + lift, z), V(x, 2.72 + lift, z), 0.008, 0.008, DWOOD, 4);
    const pen = M(new THREE.PlaneGeometry(0.2, 0.09), cloth('#b3262a', { side: THREE.DoubleSide }), g);
    pen.position.set(x + 0.1, 2.67 + lift, z);
  }
  // Walkway and merlons (iron-spiked in era III).
  M(new THREE.BoxGeometry(0.92, 0.08, 0.92), st, g).position.y = 1.9;
  for (const k of [-1, 1]) for (const i of [-1, 0, 1]) {
    M(new THREE.BoxGeometry(0.11, 0.13, 0.08), st, g).position.set(i * 0.2, 2.0, k * 0.42);
    M(new THREE.BoxGeometry(0.08, 0.13, 0.11), st, g).position.set(k * 0.42, 2.0, i * 0.2);
    if (era >= 3) {
      M(new THREE.ConeGeometry(0.018, 0.09, 4), DSTEEL, g).position.set(i * 0.2, 2.11, k * 0.42);
      M(new THREE.ConeGeometry(0.018, 0.09, 4), DSTEEL, g).position.set(k * 0.42, 2.11, i * 0.2);
    }
  }
  // Door and arrow slits.
  M(new THREE.BoxGeometry(0.22, 0.36, 0.03), DWOOD, g).position.set(0, 0.32, 0.405);
  const arch = M(new THREE.CylinderGeometry(0.11, 0.11, 0.03, 10, 1, false, 0, Math.PI), DWOOD, g);
  arch.rotation.set(Math.PI / 2, 0, -Math.PI / 2);
  arch.position.set(0, 0.5, 0.405);
  if (era >= 2) for (const y of [0.24, 0.4]) M(new THREE.BoxGeometry(0.24, 0.02, 0.01), IRON_BAND, g).position.set(0, y, 0.422);
  // Arrow slits: dark, burning red from within in era III.
  const slit = era >= 3 ? rune : BLACK;
  for (const k of [-1, 1]) for (const y of [1.05, 1.55]) {
    M(new THREE.BoxGeometry(0.04, 0.14, 0.02), slit, g).position.set(k * 0.2, y, 0.405);
    M(new THREE.BoxGeometry(0.02, 0.14, 0.04), slit, g).position.set(k * 0.405, y, 0);
  }
  // Royal-blue banners on the front and sides (long war banners from era II).
  const blue = cloth(ROYAL), trim = cloth('#d9dce3');
  const long = era >= 2;
  const ban = (x: number, z: number, ry: number): void => {
    const b = G(g, x, long ? 1.2 : 1.35, z);
    b.rotation.y = ry;
    const h = long ? 0.9 : 0.6;
    M(new THREE.BoxGeometry(0.22, h, 0.012), blue, b);
    const tail = M(new THREE.ConeGeometry(0.11, 0.12, 3), blue, b);
    tail.rotation.set(0, 0, Math.PI);
    tail.scale.z = 0.1;
    tail.position.y = -h / 2 - 0.06;
    const em = M(new THREE.BoxGeometry(0.06, 0.06, 0.016), trim, b);
    em.position.y = 0.08 + (long ? 0.12 : 0);
    em.rotation.z = Math.PI / 4;
    if (long) M(new THREE.BoxGeometry(0.22, 0.03, 0.016), trim, b).position.y = -h / 2 + 0.06;
    M(new THREE.BoxGeometry(0.26, 0.025, 0.03), DWOOD, b).position.y = h / 2;
  };
  ban(0, 0.415, 0);
  ban(0.415, 0, Math.PI / 2);
  ban(-0.415, 0, -Math.PI / 2);
  // The fire on top: on the walk in era I, on the upper stage after.
  let top = 1.94;
  if (era >= 2) {
    // The second stage: narrower, its own merlons and four bartizans.
    const h2 = 0.62;
    M(new THREE.BoxGeometry(0.54, h2, 0.54), st, g).position.y = 1.94 + h2 / 2;
    for (const k of [-1, 1]) {
      M(new THREE.BoxGeometry(0.04, 0.12, 0.02), BLACK, g).position.set(k * 0.12, 2.24, 0.272);
      M(new THREE.BoxGeometry(0.02, 0.12, 0.04), BLACK, g).position.set(0.272, 2.24, k * 0.12);
    }
    M(new THREE.BoxGeometry(0.62, 0.06, 0.62), st, g).position.y = 1.94 + h2 + 0.03;
    for (const k of [-1, 1]) for (const i of [-1, 1]) {
      M(new THREE.BoxGeometry(0.09, 0.1, 0.07), st, g).position.set(i * 0.13, 1.94 + h2 + 0.11, k * 0.29);
      M(new THREE.BoxGeometry(0.07, 0.1, 0.09), st, g).position.set(k * 0.29, 1.94 + h2 + 0.11, i * 0.13);
    }
    for (const x of [-0.29, 0.29]) for (const z of [-0.29, 0.29]) {
      M(new THREE.CylinderGeometry(0.07, 0.04, 0.22, 7), st, g).position.set(x, 1.94 + h2 - 0.02, z);
      M(new THREE.ConeGeometry(0.08, 0.16, 7), roofM, g).position.set(x, 1.94 + h2 + 0.17, z);
    }
    M(new THREE.BoxGeometry(0.57, 0.03, 0.57), IRON_BAND, g).position.y = 2.2;
    top = 1.94 + h2 + 0.06;
  }
  if (era >= 3) {
    // A third tier, flying buttresses from the turrets, a red war banner down the front.
    const h3 = 0.42;
    M(new THREE.BoxGeometry(0.36, h3, 0.36), dk, g).position.y = top + h3 / 2;
    M(new THREE.BoxGeometry(0.42, 0.05, 0.42), st, g).position.y = top + h3 + 0.025;
    for (const k of [-1, 1]) {
      M(new THREE.BoxGeometry(0.03, 0.18, 0.01), rune, g).position.set(k * 0.08, top + 0.22, 0.183);
      M(new THREE.BoxGeometry(0.01, 0.18, 0.03), rune, g).position.set(0.183, top + 0.22, k * 0.08);
    }
    for (const x of [-0.42, 0.42]) for (const z of [-0.42, 0.42]) {
      limb(g, V(x, 2.28, z), V(x * 0.45, top + h3 * 0.75, z * 0.45), 0.05, 0.075, dk, 6);
      M(new THREE.ConeGeometry(0.03, 0.14, 4), DSTEEL, g).position.set(x * 0.72, top + h3 * 0.42 + 0.1, z * 0.72);
    }
    const war = G(g, 0, 2.3, 0.28);
    M(new THREE.BoxGeometry(0.16, 0.5, 0.012), crimson, war);
    M(new THREE.BoxGeometry(0.06, 0.06, 0.016), trim, war).rotation.z = Math.PI / 4;
    top = top + h3 + 0.05;
  }
  if (era >= 4) {
    // The fourth tier: a slender stage of pale stone with silver bands, and the arched lantern of the Vigil's flame.
    const pale = stone(somn ? '#a9aeb5' : '#9a958c');
    const silver = SILVER;
    const h4 = era >= 5 ? 0.44 : 0.3;
    const w4 = somn ? 0.4 : 0.3;
    M(new THREE.BoxGeometry(w4, h4, w4), pale, g).position.y = top + h4 / 2;
    M(new THREE.BoxGeometry(w4 + 0.02, 0.025, w4 + 0.02), silver, g).position.y = top + 0.06;
    M(new THREE.BoxGeometry(w4 + 0.06, 0.04, w4 + 0.06), st, g).position.y = top + h4 + 0.02;
    // The Order's standards: long crimson banners with the white emblem, down three faces from the walk.
    for (const [x, z, ry] of [[0, 0.43, 0], [0.43, 0, Math.PI / 2], [-0.43, 0, -Math.PI / 2]] as const) {
      const b = G(g, x, 1.55, z);
      b.rotation.y = ry;
      for (const dx of [-0.2, 0.2]) {
        M(new THREE.BoxGeometry(0.12, 0.72, 0.012), crimson, b).position.set(dx, -0.1, 0.01);
        M(new THREE.BoxGeometry(0.05, 0.05, 0.016), trim, b).position.set(dx, 0.1, 0.018);
        M(new THREE.BoxGeometry(0.12, 0.02, 0.016), silver, b).position.set(dx, -0.4, 0.018);
      }
    }
    for (const y of [0.9, 1.75]) M(new THREE.BoxGeometry(0.835, 0.02, 0.835), silver, g).position.y = y;
    const base = top + h4 + 0.04;
    if (era >= 5) {
      const faro = belfryAndBeacon(g, base, pale, st, roofM, rng);
      const L = pointLight(c, 0xcfe2ff, 1.5, 6, V(0, faro, 0));
      const t = (n: number): void => {
        L.intensity = (1.2 + 0.3 * Math.sin(n * 0.4)) * LIGHT_K;
      };
      return done(c, faro + 0.72, t);
    }
    for (const x of [-0.13, 0.13]) for (const z of [-0.13, 0.13]) M(new THREE.BoxGeometry(0.045, 0.34, 0.045), pale, g).position.set(x, base + 0.17, z);
    for (const k of [-1, 1]) {
      const a1 = M(new THREE.TorusGeometry(0.11, 0.022, 4, 10, Math.PI), pale, g);
      a1.position.set(0, base + 0.34, k * 0.13);
      const a2 = M(new THREE.TorusGeometry(0.11, 0.022, 4, 10, Math.PI), pale, g);
      a2.position.set(k * 0.13, base + 0.34, 0);
      a2.rotation.y = Math.PI / 2;
    }
    M(new THREE.BoxGeometry(0.34, 0.04, 0.34), st, g).position.y = base + 0.46;
    const cap = M(new THREE.ConeGeometry(0.24, 0.32, 4), roofM, g);
    cap.position.y = base + 0.64;
    cap.rotation.y = Math.PI / 4;
    M(new THREE.SphereGeometry(0.03, 8, 6), silver, g).position.y = base + 0.82;
    // The Vigil's flame: silver-white, in a bowl on a silver stem.
    M(new THREE.CylinderGeometry(0.02, 0.03, 0.08, 6), silver, g).position.y = base + 0.04;
    const bowl4 = M(new THREE.SphereGeometry(0.075, 10, 5, 0, Math.PI * 2, Math.PI / 2, Math.PI / 2), silver, g);
    bowl4.position.y = base + 0.1;
    const fls4 = [flame(g, V(0, base + 0.1, 0), 0.75, rng, '#dfe8ff'), flame(g, V(0.025, base + 0.1, 0.02), 0.5, rng, '#ffffff')];
    glowSprite(g, '#cfdcff', 1.5, V(0, base + 0.22, 0), 0.75);
    const L = pointLight(c, 0xd8e2ff, 1.4, 5, V(0, base + 0.25, 0));
    const t = (): void => {
      for (const f of fls4) f.flick();
      L.intensity = (1.1 + rng() * 0.3) * LIGHT_K;
    };
    return done(c, base + 0.86, t);
  }
  if (era >= 3) {
    // The great beacon: a ring of iron spikes and a cage of ribs over a bowl of dragon-red fire.
    const bowl = M(new THREE.SphereGeometry(0.2, 10, 5, 0, Math.PI * 2, Math.PI / 2, Math.PI / 2), metal('#3a3a40', { side: THREE.DoubleSide }), g);
    bowl.position.y = top + 0.16;
    ring(g, 0.2, 0.02, IRON_BAND, top + 0.16, 5, 16);
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2;
      limb(g, V(Math.cos(a) * 0.2, top + 0.15, Math.sin(a) * 0.2), V(Math.cos(a) * 0.1, top + 0.52, Math.sin(a) * 0.1), 0.016, 0.02, DSTEEL, 4);
      M(new THREE.ConeGeometry(0.02, 0.1, 4), DSTEEL, g).position.set(Math.cos(a) * 0.1, top + 0.57, Math.sin(a) * 0.1);
    }
    ring(g, 0.1, 0.014, IRON_BAND, top + 0.52, 5, 12);
    const coals = emis('#ff4f3a', '#ff2a10', 1.6);
    for (let i = 0; i < 6; i++) M(new THREE.DodecahedronGeometry(0.045), coals, g).position.set((rng() - 0.5) * 0.2, top + 0.16, (rng() - 0.5) * 0.2);
    const fls = [flame(g, V(0, top + 0.16, 0), 0.95, rng, '#ff4f3a'), flame(g, V(0.06, top + 0.16, 0.04), 0.7, rng, '#ff6a2a'), flame(g, V(-0.06, top + 0.16, -0.03), 0.6, rng, '#ff4f3a')];
    glowSprite(g, '#ff3a2a', 1.6, V(0, top + 0.3, 0), 0.75);
    const L = pointLight(c, 0xff5a3a, 1.4, 5, V(0, top + 0.4, 0));
    const t = (): void => {
      for (const f of fls) f.flick();
      L.intensity = (1.1 + rng() * 0.4) * LIGHT_K;
    };
    return done(c, top + 0.64, t);
  }
  // Brazier.
  for (let i = 0; i < 3; i++) {
    const a = (i / 3) * Math.PI * 2;
    limb(g, V(Math.cos(a) * 0.16, top, Math.sin(a) * 0.16), V(Math.cos(a) * 0.08, top + 0.2, Math.sin(a) * 0.08), 0.012, 0.014, DSTEEL, 4);
  }
  const bowl = M(new THREE.SphereGeometry(0.14, 10, 5, 0, Math.PI * 2, Math.PI / 2, Math.PI / 2), metal('#3a3a40', { side: THREE.DoubleSide }), g);
  bowl.position.y = top + 0.26;
  const coals = emis('#ff6a1a', '#ff3a00', 1.2);
  for (let i = 0; i < 4; i++) M(new THREE.DodecahedronGeometry(0.035), coals, g).position.set((rng() - 0.5) * 0.14, top + 0.25, (rng() - 0.5) * 0.14);
  const fls = [flame(g, V(0, top + 0.26, 0), 1.0, rng), flame(g, V(0.05, top + 0.26, 0.03), 0.7, rng)];
  const L = pointLight(c, 0xff9a3a, 1.2, 4, V(0, top + 0.46, 0));
  const t = (): void => {
    for (const f of fls) f.flick();
    L.intensity = (1 + rng() * 0.4) * LIGHT_K;
  };
  return done(c, top + 0.56, t);
};

/** NEW: artificer's portable turret: small brass crossbow on a tripod with a gear. Head is part 'turret'. */
/**
 * The portable turret. `vigil`: the first Vigil's ballista Hilde rebuilt from the sunken camp's plans (the dragon's
 * fight, La Quemadura done): black wood and dark iron, the open eye burnt into its stock instead of the lens.
 */
export const turret = (o?: BaseOpts & { vigil?: boolean }): Model => {
  const vigil = !!o?.vigil;
  const c = begin(o, 0.4, 76);
  const { g } = c;
  const legW = vigil ? std('#1e1a18', { roughness: 0.9 }) : DWOOD;
  const brass = vigil ? std('#34302e', { metalness: 0.5, roughness: 0.55 }) : BRASS;
  const wood = vigil ? std('#2a2420', { roughness: 0.85 }) : WOOD;
  for (let i = 0; i < 3; i++) {
    const a = (i / 3) * Math.PI * 2 + Math.PI / 6;
    limb(g, V(Math.cos(a) * 0.2, 0, Math.sin(a) * 0.2), V(0, 0.32, 0), 0.012, 0.016, legW, 5);
    M(new THREE.SphereGeometry(0.018, 5, 4), brass, g).position.set(Math.cos(a) * 0.2, 0.012, Math.sin(a) * 0.2);
  }
  M(new THREE.CylinderGeometry(0.05, 0.06, 0.05, 8), brass, g).position.y = 0.33;
  const head = part(G(g, 0, 0.38, 0), 'turret');
  M(new THREE.BoxGeometry(0.06, 0.05, 0.3), wood, head);
  M(new THREE.BoxGeometry(0.07, 0.06, 0.06), brass, head).position.z = -0.1;
  if (vigil) {
    // The Vigil's open eye on the stock: a pale almond with a red iris, and a strip of red cloth on the bow.
    const eye = M(new THREE.CircleGeometry(0.03, 10), emis('#e8e0d0', '#c8b8a0', 0.5), head);
    eye.position.set(0, 0.0255, 0.03);
    eye.rotation.x = -Math.PI / 2;
    eye.scale.set(1.5, 0.75, 1);
    const iris = M(new THREE.CircleGeometry(0.012, 8), emis('#ff6a50', '#ff3a20', 1.4), head);
    iris.position.set(0, 0.0262, 0.03);
    iris.rotation.x = -Math.PI / 2;
  }
  const pts: THREE.Vector3[] = [];
  for (let i = 0; i <= 8; i++) {
    const a = -1 + (i / 8) * 2;
    pts.push(V(Math.sin(a) * 0.17, 0.0, 0.12 - Math.cos(a) * 0.06));
  }
  M(new THREE.TubeGeometry(new THREE.CatmullRomCurve3(pts), 10, vigil ? 0.013 : 0.01, 4), vigil ? std('#1e1a18', { roughness: 0.6 }) : BRASS, head);
  if (vigil) for (const k of [-1, 1]) M(new THREE.BoxGeometry(0.02, 0.022, 0.02), cloth('#a43a2c'), head).position.set(k * 0.13, 0, 0.06);
  limb(head, pts[0]!, V(0, 0, -0.02), 0.003, 0.003, STRING, 3);
  limb(head, pts[8]!, V(0, 0, -0.02), 0.003, 0.003, STRING, 3);
  const bolt = M(new THREE.ConeGeometry(0.014, 0.07, 5), DSTEEL, head);
  bolt.rotation.x = Math.PI / 2;
  bolt.position.set(0, 0.03, 0.17);
  const gear = M(new THREE.TorusGeometry(0.05, 0.014, 4, 10), vigil ? brass : BRONZE, head);
  gear.position.set(0.05, 0, -0.08);
  gear.rotation.y = Math.PI / 2;
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2;
    M(new THREE.BoxGeometry(0.012, 0.02, 0.02), vigil ? brass : BRONZE, gear).position.set(Math.cos(a) * 0.065, Math.sin(a) * 0.065, 0);
  }
  if (!vigil) {
    const lens = M(new THREE.CircleGeometry(0.018, 8), emis('#9fe3f2', '#46c4e6', 0.8), head);
    lens.position.set(0, 0.031, -0.1);
    lens.rotation.x = -Math.PI / 2;
    M(new THREE.SphereGeometry(0.015, 6, 4), GOLD, head).position.set(-0.04, 0.03, -0.1);
  }
  const t = (n: number): void => {
    gear.rotation.x = n * 0.5;
    head.rotation.y = Math.sin(n * 0.25) * 0.5;
  };
  return done(c, 0.5, t);
};

